import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { resolvePreferredLeagues } from '../utils/leaguePreference';
import LeagueCircleSelector from '../components/LeagueCircleSelector';
import TouchDragGhost, { setScaledDragImage } from '../components/TouchDragGhost';
import LoadingScreen from '../components/LoadingScreen';
import { ButtonPlaceholders, LeagueCirclePlaceholders } from '../components/LoadingPlaceholders';
import {
  getAugmentByValue,
  getPlayerAugmentsForBettingUi,
  getAugmentBaseCode,
  getAugmentDisplaySvg,
  getMatchAugmentsForHelp,
  getRoundAugmentsForHelp,
  TACTICAL_POINTS_PER_ROUND,
  getAugmentTacticalPointsCost,
  getRoundAugmentTacticalPointsCost,
  totalTacticalPointsForOpenGamesBets,
  totalTacticalPointsAfterPlacingAugment,
  totalMatchAndRoundTacticalPoints,
  DEFAULT_ROUND_AUGMENTS,
  isGameOnRoundCard,
  isVoidedGame,
  canRemoveAugmentFromGame,
  isRetiredMatchAugment,
  CLASH_TACTICAL_POINTS_MAX_PER_CLASH,
  maxClashTacticalSpendPerClashForOpenGames,
  clashTacticalPointsByKeyForOpenGames,
  PENALTY_UI_VALUE,
  MISSED_PENALTY_UI_VALUE,
  getTeamSideUiPoolState,
  getTeamSideAugmentGroup,
  isTeamSideAugmentVariant,
  isTeamSideAugmentUiDrag,
  isHomeTeamSideAugment,
  isAwayTeamSideAugment,
  resolveAugmentValueForDrop,
  getDropSideFromPointerEvent,
  getAugmentDisplayName,
  getMatchAugmentPoolHelpLead,
  getRoundAugmentPoolHelpLead,
  getOwnedStyleIdsForPoolAugment,
  getPoolAugmentDisplaySvg,
  formatAugmentRef,
  formatSplitAugmentRef,
  parseAugmentRef,
  ensureSplitAugmentRef,
  defaultSplitForPrimary,
  isValidSplitPayload,
  splitHasDistinctScoreline,
  stripSacrificeAugmentsFromBets,
  stripSacrificeFollowUpFromBets,
  findSacrificeDeadEntry,
  findHourglassEntry,
  findBountyEntry,
  isSacrificePairComplete,
  bountyStakeFromBets,
  stripParlayAugmentsFromBets,
  countParlayPlacements,
  isParlayPairComplete,
  PARLAY_AUGMENT_CODE,
  PARLAY_MAX_USES,
  HOURGLASS_AUGMENT_CODE,
  isHourglassPlacementOpenForGame,
  resolveHourglassPoints,
  canMoveHourglass,
  BOUNTY_AUGMENT_CODE,
  BOUNTY_STAKE_MIN,
  BOUNTY_STAKE_MAX,
  resolveBountyPoints,
  clampBountyStake,
  clearRoundSelectionsForSacrificedMatch,
  SPLIT_SECOND_WEIGHT_MIN,
  SPLIT_SECOND_WEIGHT_MAX,
  SPLIT_TOTAL_WEIGHT,
  getTacticalPointsCapForRound,
  roundHasKnockoutGames,
  isKnockoutMatchGame,
  isKnockoutOnlyAugmentCode,
  isShootoutUiDrag,
  SHOOTOUT_UI_VALUE,
  ROUND_NONE_SVG,
  ALL_OR_NOTHING_MAX_SLOTS,
  SAFETY_NET_MAX_SLOTS,
  allOrNothingPointsForSlotCount
} from '../utils/augments';
import { isWcLeagueCode } from '../utils/leagues';
import { formatRoundDisplay } from '../utils/formatRoundDisplay';
import { useAuth } from '../context/AuthContext';
import ClubCrest from '../components/ClubCrest';
import TeamName from '../components/TeamName';
import MatchLeagueTablePopover from '../components/MatchLeagueTablePopover';
import LiveMatchBadge from '../components/LiveMatchBadge';
import MatchEventsSection from '../components/MatchEventsSection';
import GameStatsRecap from '../components/GameStatsRecap';
import PointsBreakdown from '../components/PointsBreakdown';
import UserRoundPosition from '../components/UserRoundPosition';
import { getMatchAugmentApplied, getRoundAugmentApplied } from '../utils/betAugmentApplied';
import { useLiveGameSync } from '../hooks/useLiveGameSync';
import {
  mergeLiveGameIntoRoundsData,
  mergeServerLiveFieldsIntoRoundsData
} from '../utils/mergeLiveGame';
import {
  toastFromStandingsProcessingEvent,
  toastFromStandingsUpdatedEvent
} from '../utils/standingsProcessingToast';
import {
  applyLiveEventToRoundsCache,
  collectGamesFromRoundsCache,
  betsByLeagueStore,
  savedSnapshotsStore,
  resolveSessionSelectedLeague,
  resolveSessionSelectedRoundType,
  selectedLeagueCodeStore,
  selectedRoundTypeStore
} from '../utils/leaguePageCache';
import { shouldShowNoRoundsAvailable } from '../utils/picksLeagueLoadState';
import AugmentHelpPopover from '../components/AugmentHelpPopover';
import AugmentHelpItems from '../components/AugmentHelpItems';
import { MATCH_VOTE_ICON_SVG } from '../components/helpIconTokens';
import { OUTCOME_ICON_SVG, EXACT_ICON_SVG } from '../utils/matchPointsBreakdown';
import { ClashOpponentChips } from '../components/clash/ClashOpponentChips';
import { ClashAugmentSheet } from '../components/clash/ClashAugmentSheet';
import { formatClashLeagueDisplay } from '../utils/clashDisplay';
import { AugmentPoolStyleDot } from '../components/AugmentPoolStyleDot';
import { normalizeBetsMap } from '../components/clash/clashBettingUtils';
import {
  getPoolAugmentStylePreference,
  setPoolAugmentStylePreference,
  resolvePlacedAugmentRef,
  applyPoolStyleToRoundBets,
  restorePoolStylePreferenceFromPlacedRef
} from '../utils/augmentStylePreference';
import {
  chooseMatchTouchDropPoint,
  clearTouchDragHoldTimer,
  elementUnderTouch,
  isPointOverMatchPlays,
  resolveMatchTouchDrop,
  scheduleTouchDragHold,
  shouldCancelTouchDragHold,
  shouldIgnoreNativeDragDuringTouch,
  claimTouchDragHandle,
  claimPoolTouchDrag,
  applyPoolCarouselSwipe
} from '../utils/touchDragActivation';
import {
  clampBetScore,
  hasPlacedBet,
  roundSnapshotKey,
  serializeRoundBetsSnapshot,
  buildSavedSnapshotsFromRoundsData,
  hasUnsavedRoundChanges
} from '../utils/bettingSnapshots';
import {
  isGameLive,
  pickNextMatchForPicks,
  sortGamesForPicksDisplay
} from '../utils/homeGameDisplayOrder';
import { getGameStatusLabel } from '../utils/gameStatusLabel';
import { ROUND_LOCK_BUFFER_MS } from '../config/roundTiming';
import { isKickoffInMainCluster } from 'arena-shared/kickoffCluster';
import {
  formatPicksLockExactTime,
  formatPicksLockDuration
} from '../utils/formatPicksLockCountdown';
import './Betting.css';

/** Auto-dismiss for floating toasts on the betting page. */
const BETTING_SUCCESS_TOAST_MS = 2000;
const BETTING_TIER_CLASH_TOAST_MS = 6000;
const TIER_CLASH_SAVE_MESSAGE =
  "Your picks have been saved. You've been given a clash in your tier league — you can add clash plays.";
const BETTING_TACTICAL_POINTS_TOAST_MS = 4000;
const BETTING_SOFT_WARN_TOAST_MS = 5000;
const MAX_MATCH_VOTES = 3;

/** Auto-dismiss floating validation toasts (tactical cap + One Goal soft warns). */
function isAutoDismissBettingError(msg) {
  return (
    /tactical points/i.test(msg)
  );
}
function sameGameId(a, b) {
  return String(a) === String(b);
}

/** Numeric match slot from gameId (`PL202518#3` → 3). */
function gameSlotFromId(gameId) {
  const s = String(gameId || '');
  const idx = s.lastIndexOf('#');
  if (idx < 0) return null;
  const n = Number(s.slice(idx + 1));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

function roundTypeToDataKey(roundType) {
  if (roundType === 'previous') return 'previousRound';
  if (roundType === 'current') return 'currentRound';
  return 'nextRound';
}

/** Prefer current when present; else next, then previous. */
function resolveDefaultRoundType(roundsData) {
  if (!roundsData) return 'current';
  if (roundsData.currentRound) return 'current';
  if (roundsData.nextRound) return 'next';
  if (roundsData.previousRound) return 'previous';
  return 'current';
}

function picksRoundTypeAvailable(roundsData, roundType) {
  if (!roundsData) return false;
  if (roundType === 'previous') return !!roundsData.previousRound;
  if (roundType === 'current') return !!roundsData.currentRound;
  return !!roundsData.nextRound;
}

function resolvePicksRoundType(roundsData) {
  return resolveSessionSelectedRoundType(
    (type) => picksRoundTypeAvailable(roundsData, type),
    resolveDefaultRoundType(roundsData)
  );
}

/** Postponed/canceled fixtures keep stale kickoffs and must not set the round lock. */
function isLockEligibleGame(game) {
  if (game == null || game.gameDate == null) return false;
  const status = String(game.status || '').toLowerCase();
  return status !== 'postponed' && status !== 'canceled' && status !== 'cancelled';
}

function lockEligibleKickoffsMs(games) {
  return games
    .filter(isLockEligibleGame)
    .map((g) => new Date(g.gameDate).getTime())
    .filter((ms) => Number.isFinite(ms));
}

function firstMainSlateKickoffMs(games) {
  const allKickoffs = lockEligibleKickoffsMs(games);
  if (allKickoffs.length === 0) return null;
  const slate = allKickoffs.filter((ms) => isKickoffInMainCluster(ms, allKickoffs));
  if (slate.length === 0) return null;
  return Math.min(...slate);
}

/** When API omits roundSubmissionOpen (legacy deploy), derive from game kickoffs. */
function computeRoundSubmissionOpenFromGames(games) {
  if (!Array.isArray(games) || games.length === 0) return false;
  const firstKickoff = firstMainSlateKickoffMs(games);
  if (firstKickoff == null) return false;
  return Date.now() < firstKickoff - ROUND_LOCK_BUFFER_MS;
}

const AUGMENTS_PER_VIEW_DESKTOP = 5;
const AUGMENTS_PER_VIEW_MOBILE = 4;
const AUGMENTS_CAROUSEL_MOBILE_MQ = '(max-width: 768px)';

function getAugmentsPerView() {
  if (typeof window === 'undefined') return AUGMENTS_PER_VIEW_DESKTOP;
  if (typeof window.matchMedia !== 'function') return AUGMENTS_PER_VIEW_DESKTOP;
  const mq = window.matchMedia(AUGMENTS_CAROUSEL_MOBILE_MQ);
  if (!mq || typeof mq.matches !== 'boolean') return AUGMENTS_PER_VIEW_DESKTOP;
  return mq.matches ? AUGMENTS_PER_VIEW_MOBILE : AUGMENTS_PER_VIEW_DESKTOP;
}

function augmentStripEdges(el) {
  if (!el) return { atStart: true, atEnd: false };
  const max = el.scrollWidth - el.clientWidth;
  return {
    atStart: el.scrollLeft <= 1,
    atEnd: max <= 1 || el.scrollLeft >= max - 1
  };
}

function scrollAugmentStripByCard(el, direction) {
  if (!el) return;
  const card = el.querySelector('.augment-card');
  if (!card) return;
  const track = el.querySelector('.augment-cards-track');
  const gap = track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
  const step = card.getBoundingClientRect().width + gap;
  el.scrollBy({ left: direction * step, behavior: 'smooth' });
}

function useAugmentsPerView() {
  const [augmentsPerView, setAugmentsPerView] = useState(getAugmentsPerView);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(AUGMENTS_CAROUSEL_MOBILE_MQ);
    if (!mq || typeof mq.addEventListener !== 'function') return undefined;
    const onChange = () => setAugmentsPerView(getAugmentsPerView());
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return augmentsPerView;
}

function availableBountyStakeForRound(round, seasonStack) {
  return (Number(seasonStack) || 0) + bountyStakeFromBets(round?.serverBets);
}

function SeasonStackChip({ stack, className = '' }) {
  const n = Number(stack) || 0;
  return (
    <div
      className={`picks-season-stack${className ? ` ${className}` : ''}`}
      data-testid="picks-season-stack"
      aria-label={`Season stack: ${n}`}
    >
      <span className="picks-season-stack__label">Stack</span>
      <span className="picks-season-stack__count">{n}</span>
    </div>
  );
}

export function BettingPageLoading() {
  return (
    <div className="betting-page">
      <header className="betting-header">
        <h1 className="betting-title">Place Your Picks</h1>
        <p className="betting-subtitle betting-subtitle--picks-lock">{'\u00a0'}</p>
        <LeagueCirclePlaceholders />
      </header>
      <ButtonPlaceholders />
      <LoadingScreen />
    </div>
  );
}

const Betting = () => {
  const { user, updateToken } = useAuth();
  const preferredLeagues = useMemo(
    () => resolvePreferredLeagues(user?.lp, user?.ld),
    [user?.lp, user?.ld]
  );
  const augmentsPerView = useAugmentsPerView();
  const [augmentCarouselOffset, setAugmentCarouselOffset] = useState(0);
  const [roundAugmentCarouselOffset, setRoundAugmentCarouselOffset] = useState(0);
  const roundAugmentStripRef = useRef(null);
  const matchAugmentStripRef = useRef(null);
  const [roundStripAtStart, setRoundStripAtStart] = useState(true);
  const [roundStripAtEnd, setRoundStripAtEnd] = useState(false);
  const [matchStripAtStart, setMatchStripAtStart] = useState(true);
  const [matchStripAtEnd, setMatchStripAtEnd] = useState(false);
  const mobileAugmentScroll = augmentsPerView === AUGMENTS_PER_VIEW_MOBILE;
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [roundsData, setRoundsData] = useState(null);
  /** League code whose roundsData is currently applied (avoids empty-state flash on switch). */
  const [loadedLeagueCode, setLoadedLeagueCode] = useState(null);
  /** leagueCode → last known roundsData (skip refetch when switching back; session-lived) */
  const [betsByLeague, setBetsByLeagueState] = useState(() => ({ ...betsByLeagueStore.get() }));
  const betsByLeagueRef = useRef(betsByLeagueStore.get());
  const selectedLeagueRef = useRef(null);
  const roundsDataRef = useRef(null);
  const savedSnapshotsRef = useRef(savedSnapshotsStore.get());
  const selectedRoundTypeRef = useRef(selectedRoundTypeStore.get() || 'current');
  const setBetsByLeague = useCallback((updater) => {
    setBetsByLeagueState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      betsByLeagueStore.set(next);
      betsByLeagueRef.current = next;
      return next;
    });
  }, []);
  betsByLeagueRef.current = betsByLeague;
  selectedLeagueRef.current = selectedLeague;
  /** `pk:<roundCode>` or `${leagueCode}:${roundDataKey}` → serialized bets snapshot from last load/save (session-lived) */
  const [savedSnapshots, setSavedSnapshotsState] = useState(() => ({ ...savedSnapshotsStore.get() }));
  const setSavedSnapshots = useCallback((updater) => {
    setSavedSnapshotsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      savedSnapshotsStore.set(next);
      return next;
    });
  }, []);
  const [selectedRoundType, setSelectedRoundType] = useState(
    () => selectedRoundTypeStore.get() || 'current'
  );
  const selectRoundType = useCallback((type) => {
    selectedRoundTypeStore.set(type);
    setSelectedRoundType(type);
  }, []);
  roundsDataRef.current = roundsData;
  savedSnapshotsRef.current = savedSnapshots;
  selectedRoundTypeRef.current = selectedRoundType;
  const playerAugments = useMemo(() => {
    if (!roundsData) return getPlayerAugmentsForBettingUi();
    let sr = null;
    if (selectedRoundType === 'previous') sr = roundsData.previousRound;
    else if (selectedRoundType === 'current') sr = roundsData.currentRound;
    else if (selectedRoundType === 'next') sr = roundsData.nextRound;
    sr = sr || roundsData.currentRound || roundsData.nextRound || roundsData.previousRound;
    return getPlayerAugmentsForBettingUi({ includeKnockout: roundHasKnockoutGames(sr) });
  }, [roundsData, selectedRoundType]);
  const [selectedAugment, setSelectedAugment] = useState(null);
  const [draggingAugment, setDraggingAugment] = useState(null);
  const [draggingFromGameId, setDraggingFromGameId] = useState(null);
  const [dragOverGameId, setDragOverGameId] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [touchDragData, setTouchDragData] = useState(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchCurrentPos, setTouchCurrentPos] = useState(null);
  /** Pending long-press before mobile drag arms: `{ startPos, payload, draggingAugment, draggingFromGameId }`. */
  const touchDragPendingRef = useRef(null);
  const touchDragHoldTimerRef = useRef(null);
  /** Sync payload for touchmove/end between arm and React re-render. */
  const touchDragDataRef = useRef(null);
  /** Chip under the finger; cloned at its layout size for the drag preview. */
  const touchDragSourceElRef = useRef(null);
  /** Last finger position after the drag has armed. touchend coordinates can lie. */
  const touchDragLastPointRef = useRef(null);
  const applyMatchTouchDropRef = useRef(() => {});
  /** Sideways swipe of Match plays after the finger moves before the drag hold. */
  const poolSwipeRef = useRef(null);
  /** Radial wave on the game card a match play was dropped on */
  const [matchAugmentSpreadWave, setMatchAugmentSpreadWave] = useState(null);
  const matchAugmentSpreadTimeoutRef = useRef(null);
  /** Radial wave on the round play card row only */
  const [roundAugmentSpreadWave, setRoundAugmentSpreadWave] = useState(null);
  const roundAugmentSpreadTimeoutRef = useRef(null);
  const roundAugmentPulseIdRef = useRef(0);
  const errorDismissTimeoutRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  /** `{ gameId, augmentMapKey, opponentUsername, clashLeagueName?, hideCompetitionLabel? }` while clash play sheet is open */
  const [clashSheet, setClashSheet] = useState(null);
  /** Bumps when pool art selection changes (re-read prefs + preview). */
  const [poolStyleTick, setPoolStyleTick] = useState(0);
  /** Drives live picks-lock countdown while a submission window is open. */
  const [nowMs, setNowMs] = useState(() => Date.now());

  const selectMatchPoolStyle = useCallback(
    (poolValue, styleId) => {
      setPoolAugmentStylePreference(poolValue, styleId);
      setPoolStyleTick((n) => n + 1);
      const roundKey =
        selectedRoundType === 'previous'
          ? 'previousRound'
          : selectedRoundType === 'current'
            ? 'currentRound'
            : 'nextRound';
      const inventory = user?.inventory ?? {};
      setRoundsData((prev) => {
        const round = prev?.[roundKey];
        if (!round?.roundSubmissionOpen) return prev;
        const nextRound = applyPoolStyleToRoundBets(round, poolValue, styleId, inventory);
        if (nextRound === round) return prev;
        return { ...prev, [roundKey]: nextRound };
      });
    },
    [selectedRoundType, user?.inventory]
  );

  const getMatchPoolStyleId = useCallback(
    (poolValue) => {
      void poolStyleTick;
      const pref = getPoolAugmentStylePreference(poolValue);
      return pref === undefined ? null : pref;
    },
    [poolStyleTick]
  );

  const buildPoolDragPayload = useCallback(
    (poolValue, source, extra = {}) => {
      const styleId = getMatchPoolStyleId(poolValue);
      return { value: poolValue, source, styleId: styleId ?? null, ...extra };
    },
    [getMatchPoolStyleId]
  );

  const clearMatchAugmentTouchDragHold = useCallback(() => {
    clearTouchDragHoldTimer(touchDragHoldTimerRef);
    touchDragPendingRef.current = null;
  }, []);

  const resetMatchAugmentTouchDragState = useCallback(() => {
    clearMatchAugmentTouchDragHold();
    touchDragDataRef.current = null;
    touchDragSourceElRef.current = null;
    touchDragLastPointRef.current = null;
    setDraggingAugment(null);
    setDraggingFromGameId(null);
    setTouchDragData(null);
    setDragOverGameId(null);
    setDragOverSide(null);
    setIsTouchDragging(false);
    setTouchCurrentPos(null);
    setTouchStartPos(null);
  }, [clearMatchAugmentTouchDragHold]);

  const armMatchAugmentTouchDrag = useCallback(() => {
    const pending = touchDragPendingRef.current;
    if (!pending) return;
    touchDragPendingRef.current = null;
    touchDragDataRef.current = pending.payload;
    touchDragSourceElRef.current = pending.sourceEl ?? null;
    setTouchCurrentPos(pending.startPos);
    setDraggingAugment(pending.draggingAugment);
    setDraggingFromGameId(pending.draggingFromGameId);
    setTouchDragData(pending.payload);
    setIsTouchDragging(true);
  }, []);

  useEffect(() => () => clearTouchDragHoldTimer(touchDragHoldTimerRef), []);

  useEffect(() => {
    const stopScrollWhileDragging = (event) => {
      if (!touchDragDataRef.current) return;
      if (event.cancelable) event.preventDefault();
    };
    window.addEventListener('touchmove', stopScrollWhileDragging, { passive: false });
    return () => window.removeEventListener('touchmove', stopScrollWhileDragging);
  }, []);

  useEffect(() => {
    if (preferredLeagues.length === 0) return;
    setSelectedLeague((current) => resolveSessionSelectedLeague(preferredLeagues, current));
  }, [preferredLeagues]);

  useEffect(() => {
    setAugmentCarouselOffset(0);
    setRoundAugmentCarouselOffset(0);
  }, [selectedLeague?.code, selectedRoundType]);

  useEffect(() => {
    setClashSheet(null);
  }, [selectedLeague?.code, selectedRoundType]);

  useEffect(() => {
    if (!roundsData) return undefined;
    const roundKey = roundTypeToDataKey(selectedRoundType);
    const round = roundsData[roundKey];
    if (!round?.roundSubmissionOpen) return undefined;
    const firstKickoff = firstMainSlateKickoffMs(round.games || []);
    if (firstKickoff == null) return undefined;
    const lockMs = firstKickoff - ROUND_LOCK_BUFFER_MS;
    if (!(lockMs > Date.now())) return undefined;

    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [roundsData, selectedRoundType]);

  useEffect(() => {
    const max = Math.max(0, playerAugments.length - augmentsPerView);
    setAugmentCarouselOffset((o) => Math.min(o, max));
  }, [playerAugments.length, augmentsPerView]);

  useEffect(() => {
    const count = 1 + DEFAULT_ROUND_AUGMENTS.length;
    const max = Math.max(0, count - augmentsPerView);
    setRoundAugmentCarouselOffset((o) => Math.min(o, max));
  }, [augmentsPerView]);

  useEffect(() => {
    if (augmentsPerView !== AUGMENTS_PER_VIEW_MOBILE) return undefined;
    const measure = () => {
      const round = augmentStripEdges(roundAugmentStripRef.current);
      setRoundStripAtStart(round.atStart);
      setRoundStripAtEnd(round.atEnd);
      const match = augmentStripEdges(matchAugmentStripRef.current);
      setMatchStripAtStart(match.atStart);
      setMatchStripAtEnd(match.atEnd);
    };
    measure();
    const id = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
    };
  }, [augmentsPerView, playerAugments.length]);

  useEffect(() => {
    if (selectedLeague) loadBetsForSelectedLeague();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when league changes; cache handles revisits
  }, [selectedLeague?.code]);

  /** Keep per-league cache in sync with edits / live merges on the active league. */
  useEffect(() => {
    if (!selectedLeague?.code || !roundsData) return;
    // Avoid caching the previous league's rounds under the newly selected code.
    if (loadedLeagueCode !== selectedLeague.code) return;
    setBetsByLeague((prev) => {
      // Only write-through after this league has been loaded once (fetch seeds the cache).
      if (!(selectedLeague.code in prev)) return prev;
      if (prev[selectedLeague.code] === roundsData) return prev;
      return { ...prev, [selectedLeague.code]: roundsData };
    });
  }, [selectedLeague?.code, roundsData, loadedLeagueCode]);

  /** Drop stale soft toasts when switching round tabs (copy/numbers refer to the prior view). */
  useEffect(() => {
    setError((prev) => {
      if (!prev || !isAutoDismissBettingError(prev)) return prev;
      return '';
    });
  }, [selectedRoundType]);

  /** Auto-dismiss soft validation toasts (still closable via ×). */
  useEffect(() => {
    if (errorDismissTimeoutRef.current) {
      clearTimeout(errorDismissTimeoutRef.current);
      errorDismissTimeoutRef.current = null;
    }
    if (!error || !isAutoDismissBettingError(error)) return undefined;
    const ms = /tactical points/i.test(error)
      ? BETTING_TACTICAL_POINTS_TOAST_MS
      : BETTING_SOFT_WARN_TOAST_MS;
    errorDismissTimeoutRef.current = setTimeout(() => {
      setError((prev) => (isAutoDismissBettingError(prev) ? '' : prev));
      errorDismissTimeoutRef.current = null;
    }, ms);
    return () => {
      if (errorDismissTimeoutRef.current) {
        clearTimeout(errorDismissTimeoutRef.current);
        errorDismissTimeoutRef.current = null;
      }
    };
  }, [error]);

  useEffect(() => () => {
    if (matchAugmentSpreadTimeoutRef.current) clearTimeout(matchAugmentSpreadTimeoutRef.current);
    if (roundAugmentSpreadTimeoutRef.current) clearTimeout(roundAugmentSpreadTimeoutRef.current);
    if (errorDismissTimeoutRef.current) clearTimeout(errorDismissTimeoutRef.current);
  }, []);

  const triggerMatchAugmentSpreadWave = (gameIds, color, svg) => {
    const c = color || 'gold';
    const targets = new Set(gameIds.map((id) => String(id)));
    if (matchAugmentSpreadTimeoutRef.current) {
      clearTimeout(matchAugmentSpreadTimeoutRef.current);
      matchAugmentSpreadTimeoutRef.current = null;
    }
    setMatchAugmentSpreadWave({ targets, color: c, svg: svg || null });
    matchAugmentSpreadTimeoutRef.current = setTimeout(() => {
      setMatchAugmentSpreadWave(null);
      matchAugmentSpreadTimeoutRef.current = null;
    }, 1200);
  };

  const triggerRoundAugmentSpreadWave = (color) => {
    const c = color || 'gold';
    if (roundAugmentSpreadTimeoutRef.current) {
      clearTimeout(roundAugmentSpreadTimeoutRef.current);
      roundAugmentSpreadTimeoutRef.current = null;
    }
    roundAugmentPulseIdRef.current += 1;
    const pulseId = roundAugmentPulseIdRef.current;
    setRoundAugmentSpreadWave({ color: c, pulseId });
    roundAugmentSpreadTimeoutRef.current = setTimeout(() => {
      setRoundAugmentSpreadWave(null);
      roundAugmentSpreadTimeoutRef.current = null;
    }, 1200);
  };

  const attachServerBets = (normalized, raw) => {
    if (!normalized) return normalized;
    const out = { ...normalized };
    for (const key of ['currentRound', 'previousRound', 'nextRound']) {
      const round = out[key];
      if (!round) continue;
      out[key] = {
        ...round,
        serverBets: { ...(raw?.[key]?.bets ?? {}) }
      };
    }
    return out;
  };

  const ensureDefaultBets = (data) => {
    if (!data) return data;
    const roundKeys = ['currentRound', 'previousRound', 'nextRound'];
    const out = { ...data };
    for (const key of roundKeys) {
      const round = data[key];
      if (!round?.games) continue;
      let bets = normalizeBetsMap(round.bets || {});
      const openSlotSet = new Set(
        round.games
          .filter((g) => isGameOnRoundCard(g))
          .map((g) => gameSlotFromId(g.gameId))
          .filter((slot) => slot != null)
      );
      for (const g of round.games) {
        if (!isGameOnRoundCard(g)) continue;
        const gidKey = String(g.gameId);
        if (!bets[gidKey] || bets[gidKey].predictedHomeScore == null || bets[gidKey].predictedAwayScore == null) {
          bets[gidKey] = {
            predictedHomeScore: bets[gidKey]?.predictedHomeScore ?? 0,
            predictedAwayScore: bets[gidKey]?.predictedAwayScore ?? 0,
            ...(bets[gidKey]?.augment && { augment: bets[gidKey].augment }),
            ...(bets[gidKey]?.augmentApplied === true || bets[gidKey]?.augmentApplied === false
              ? { augmentApplied: bets[gidKey].augmentApplied }
              : {}),
            ...(Array.isArray(bets[gidKey]?.appliedAugments) &&
              bets[gidKey].appliedAugments.length > 0 && {
                appliedAugments: bets[gidKey].appliedAugments.map(String)
              }),
            ...(bets[gidKey]?.augmentPoints != null &&
              Number.isFinite(Number(bets[gidKey].augmentPoints)) && {
                augmentPoints: Number(bets[gidKey].augmentPoints)
              }),
            ...(bets[gidKey]?.augmentRate != null &&
              Number.isFinite(Number(bets[gidKey].augmentRate)) && {
                augmentRate: Number(bets[gidKey].augmentRate)
              }),
            ...(bets[gidKey]?.bountyPoints != null &&
              Number.isFinite(Number(bets[gidKey].bountyPoints)) && {
                bountyPoints: Number(bets[gidKey].bountyPoints)
              }),
            ...(bets[gidKey]?.clashAugments &&
              typeof bets[gidKey].clashAugments === 'object' && {
                clashAugments: { ...bets[gidKey].clashAugments }
              })
          };
        } else if (bets[gidKey]?.clashAugments && typeof bets[gidKey].clashAugments === 'object') {
          bets[gidKey] = { ...bets[gidKey], clashAugments: { ...bets[gidKey].clashAugments } };
        }
        if (bets[gidKey]?.augment && isRetiredMatchAugment(bets[gidKey].augment)) {
          const { augment: _removed, ...rest } = bets[gidKey];
          bets[gidKey] = rest;
        }
      }
      const votes = (Array.isArray(round.votes) ? round.votes.map(String) : []).filter((gid) =>
        round.games.some((g) => sameGameId(g.gameId, gid))
      );
      const roundSubmissionOpen =
        typeof round.roundSubmissionOpen === 'boolean'
          ? round.roundSubmissionOpen
          : computeRoundSubmissionOpenFromGames(round.games);
      let roundAugment = round.roundAugment ?? null;
      // Only prune AON/SN slots while picks are still editable. Locked/finished rounds
      // keep historical targets so match badges can show which games were selected.
      if (roundAugment && roundSubmissionOpen) {
        const parsed = parseAugmentRef(roundAugment);
        if ((parsed.code === 'aon' || parsed.code === 'sn') && parsed.targets?.length) {
          const nextTargets = parsed.targets.filter((slot) => openSlotSet.has(slot));
          if (nextTargets.length !== parsed.targets.length) {
            roundAugment = formatAugmentRef(parsed.code, parsed.styleId, nextTargets);
          }
        }
      }
      out[key] = {
        ...round,
        bets,
        votes,
        roundAugment,
        ...(round.roundPoints != null ? { roundPoints: round.roundPoints } : {}),
        ...(round.roundAugmentApplied === true || round.roundAugmentApplied === false
          ? { roundAugmentApplied: round.roundAugmentApplied }
          : {}),
        roundSubmissionOpen
      };
    }
    return out;
  };

  const applyBetsPayload = (leagueCode, normalized) => {
    setBetsByLeague((prev) => ({ ...prev, [leagueCode]: normalized }));
    setSavedSnapshots((prev) => ({
      ...prev,
      ...buildSavedSnapshotsFromRoundsData(normalized, leagueCode)
    }));
    if (selectedLeagueRef.current?.code === leagueCode) {
      setRoundsData(normalized);
      setLoadedLeagueCode(leagueCode);
      // Always re-resolve: keeps a still-available pill, falls back when rounds roll
      // (e.g. current → null gap leaves previous + next only).
      setSelectedRoundType(resolvePicksRoundType(normalized));
    }
  };

  const handleLeagueSelect = (league) => {
    if (selectedLeague?.code === league.code) return;
    // Match Home: set loading before selection commits so we never paint empty mid-switch.
    if (loadedLeagueCode !== null && !betsByLeagueRef.current[league.code]) {
      setLoading(true);
    }
    selectedLeagueCodeStore.set(league.code);
    setSelectedLeague(league);
  };

  const fetchBetsData = async ({ force = false } = {}) => {
    const league = selectedLeagueRef.current;
    if (!league) return;
    const leagueCode = league.code;

    if (!force) {
      const cached = betsByLeagueRef.current[leagueCode];
      if (cached) {
        setRoundsData(cached);
        setLoadedLeagueCode(leagueCode);
        setLoading(false);
        setError('');
        setSelectedRoundType(resolvePicksRoundType(cached));
        return;
      }
    }

    // Keep existing picks on screen during background/resume refetches (phone lock → Chrome).
    // Only blank with the full-page loader when we have nothing useful to show for this league.
    const keepShowingCached = Boolean(betsByLeagueRef.current[leagueCode]);

    try {
      if (!keepShowingCached) {
        setLoading(true);
      }
      setError('');
      const response = await api.get(`/mypicks/${leagueCode}`);
      const normalized = attachServerBets(ensureDefaultBets(response.data), response.data);
      normalized.seasonStack = Number(response.data?.seasonStack) || 0;

      // Resume / reconnect must not wipe in-progress pick edits — merge live game fields only.
      const localRounds = roundsDataRef.current;
      const roundKey = roundTypeToDataKey(selectedRoundTypeRef.current);
      const localRound = localRounds?.[roundKey] ?? null;
      const preserveDrafts =
        Boolean(force) &&
        localRound != null &&
        hasUnsavedRoundChanges(
          localRound,
          savedSnapshotsRef.current,
          leagueCode,
          roundKey
        );

      if (preserveDrafts) {
        const merged = mergeServerLiveFieldsIntoRoundsData(localRounds, normalized);
        setBetsByLeague((prev) => ({ ...prev, [leagueCode]: merged }));
        if (selectedLeagueRef.current?.code === leagueCode) {
          setRoundsData(merged);
          setLoadedLeagueCode(leagueCode);
        }
      } else {
        applyBetsPayload(leagueCode, normalized);
      }
    } catch (err) {
      if (err.response?.status === 401) return; // Handled by api interceptor (logout)
      if (selectedLeagueRef.current?.code === leagueCode) {
        setError('Failed to load picks data');
      }
      console.error(err);
    } finally {
      if (selectedLeagueRef.current?.code === leagueCode) {
        setLoading(false);
      }
    }
  };

  const loadBetsForSelectedLeague = () => {
    const league = selectedLeagueRef.current;
    if (!league) return;
    const cached = betsByLeagueRef.current[league.code];
    if (cached) {
      setRoundsData(cached);
      setLoadedLeagueCode(league.code);
      setLoading(false);
      setError('');
      setSelectedRoundType(resolvePicksRoundType(cached));
      return;
    }
    fetchBetsData({ force: true });
  };

  const liveSyncGames = useMemo(
    () => collectGamesFromRoundsCache(betsByLeague, roundsData),
    [betsByLeague, roundsData]
  );

  const standingsToastGameIdRef = useRef(null);

  const handleLiveGameEvent = useCallback((event) => {
    const processingToast = toastFromStandingsProcessingEvent(event);
    if (processingToast) {
      standingsToastGameIdRef.current = processingToast.id;
      setSuccess(processingToast.message);
      return;
    }

    const updatedToast = toastFromStandingsUpdatedEvent(event);
    if (updatedToast) {
      const pendingId = standingsToastGameIdRef.current;
      if (!pendingId || pendingId !== updatedToast.id) return;
      standingsToastGameIdRef.current = null;
      setSuccess(updatedToast.message);
      setTimeout(() => setSuccess(''), BETTING_SUCCESS_TOAST_MS);
      return;
    }

    const snapshot = event?.game;
    const gameId = event?.gameId || snapshot?.gameId;
    if (!gameId || !snapshot) return;
    setRoundsData((prev) => mergeLiveGameIntoRoundsData(prev, gameId, snapshot));
    setBetsByLeague((prev) => applyLiveEventToRoundsCache(prev, gameId, snapshot));
  }, []);

  useLiveGameSync({
    games: liveSyncGames,
    onLiveEvent: handleLiveGameEvent,
    onVisibilityResume: () => fetchBetsData({ force: true }),
    enabled: Boolean(selectedLeague),
    onSubscribed: () => fetchBetsData({ force: true })
  });

  const getSelectedRoundData = () => {
    if (!roundsData) return null;
    if (selectedRoundType === 'previous' && roundsData.previousRound) return roundsData.previousRound;
    if (selectedRoundType === 'current' && roundsData.currentRound) return roundsData.currentRound;
    if (selectedRoundType === 'next' && roundsData.nextRound) return roundsData.nextRound;
    return roundsData.currentRound || roundsData.nextRound || roundsData.previousRound;
  };

  const stickyScrollHostRef = useRef(null);
  const stickyMeterRef = useRef(null);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Match plays use top: nav + var(--augment-sticky-meter-stack); a fixed rem left a hairline gap—sync to real meter height */
  useLayoutEffect(() => {
    const host = stickyScrollHostRef.current;
    const meter = stickyMeterRef.current;
    if (!host) return;
    if (!meter) {
      host.style.removeProperty('--augment-sticky-meter-stack');
      return;
    }
    const sr = getSelectedRoundData();
    const finished =
      sr?.games?.length > 0 && sr.games.every((g) => !isGameOnRoundCard(g));
    if (finished) {
      host.style.removeProperty('--augment-sticky-meter-stack');
      return;
    }
    const apply = () => {
      /* offsetHeight: integer layout height incl. padding+border; ceil(h)+1 was too large → gap under meter when stuck */
      host.style.setProperty('--augment-sticky-meter-stack', `${meter.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(meter);
    return () => ro.disconnect();
  }, [loading, roundsData, selectedRoundType, selectedLeague?.code]);

  const clampScore = clampBetScore;

  const handleBetChange = (gameId, homeScore, awayScore) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound || !selectedRound.roundSubmissionOpen) return;
    const game = selectedRound.games.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;

    const roundKey = selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    const gidKey = String(gameId);

    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      const bets = normalizeBetsMap(prev[roundKey].bets || {});
      const cur = bets[gidKey];
      const h = clampScore(homeScore);
      const a = clampScore(awayScore);
      let augment = cur?.augment;
      if (augment && isKnockoutOnlyAugmentCode(augment)) {
        const code = getAugmentBaseCode(augment);
        if ((code === 'ph' || code === 'pa') && h !== a) {
          augment = undefined;
        }
      }
      if (augment && getAugmentBaseCode(augment) === 's') {
        augment = ensureSplitAugmentRef(augment, h, a);
      }
      const nextBet = {
        predictedHomeScore: h,
        predictedAwayScore: a,
        ...(cur?.clashAugments &&
          typeof cur.clashAugments === 'object' && {
            clashAugments: { ...cur.clashAugments }
          }),
        ...(cur?.bountyPoints != null &&
          Number.isFinite(Number(cur.bountyPoints)) && {
            bountyPoints: Number(cur.bountyPoints)
          })
      };
      if (augment) nextBet.augment = augment;
      return {
        ...prev,
        [roundKey]: {
          ...prev[roundKey],
          bets: { ...bets, [gidKey]: nextBet }
        }
      };
    });
  };

  const handleAugmentDrop = (targetGameId, dragData, dropSide = null, gameCardEl = null) => {
    if (!dragData) return;
    const { value: rawValue, source, sourceGameId } = dragData;
    const side =
      dropSide ?? (isTeamSideAugmentUiDrag(rawValue) && gameCardEl ? getDropSideFromPointerEvent({ target: gameCardEl }, gameCardEl) : null);
    const value = resolveAugmentValueForDrop(rawValue, side);
    if (!value) return;
    if (
      source === 'game' &&
      sameGameId(sourceGameId, targetGameId) &&
      (!isTeamSideAugmentUiDrag(rawValue) ||
        (isTeamSideAugmentVariant(rawValue) && getAugmentBaseCode(rawValue) === value))
    ) {
      return;
    }
    const inventory = user?.inventory ?? {};
    const ref = resolvePlacedAugmentRef(value, dragData, inventory);
    const selectedRound = getSelectedRoundData();
    if (getAugmentBaseCode(ref) === HOURGLASS_AUGMENT_CODE && selectedRound && !selectedRound.roundSubmissionOpen) {
      handleHourglassPlace(targetGameId, ref);
      return;
    }
    handleAugmentPlace(targetGameId, ref);
  };

  const handleHourglassPlace = async (gameId, augmentValue) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.partitionKey) return;
    const game = selectedRound.games?.find((g) => sameGameId(g.gameId, gameId));
    if (!game) return;
    const deadEntry = findSacrificeDeadEntry(selectedRound.bets);
    if (deadEntry && sameGameId(deadEntry.gameId, gameId)) {
      setError('Hourglass cannot go on the sacrificed match.');
      return;
    }
    const existingHourglass = findHourglassEntry(selectedRound.bets);
    if (existingHourglass && sameGameId(existingHourglass.gameId, gameId)) {
      return;
    }
    if (existingHourglass) {
      const fromGame = selectedRound.games?.find((g) => sameGameId(g.gameId, existingHourglass.gameId));
      if (!fromGame || !isHourglassPlacementOpenForGame(fromGame)) {
        setError('Hourglass is locked on the current match.');
        return;
      }
      if (!canMoveHourglass(existingHourglass.hourglassPoints)) {
        setError('Hourglass is spent — no hops left.');
        return;
      }
    }
    if (!isHourglassPlacementOpenForGame(game)) {
      setError('Hourglass must be placed before the second half starts on this match.');
      return;
    }
    const existingAug = selectedRound.bets?.[String(gameId)]?.augment;
    if (existingAug) {
      setError('This match already has a play — Hourglass needs an empty slot.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { data } = await api.post('/picks/hourglass', {
        partitionKey: selectedRound.partitionKey,
        leagueCode: selectedLeague?.code,
        gameId,
        augment: augmentValue
      });
      const roundKey =
        selectedRoundType === 'previous'
          ? 'previousRound'
          : selectedRoundType === 'current'
            ? 'currentRound'
            : 'nextRound';
      const placedRef = data.augment ?? augmentValue;
      const hourglassPoints = resolveHourglassPoints(data.hourglassPoints);
      setRoundsData((prev) => {
        if (!prev?.[roundKey]) return prev;
        const bets = normalizeBetsMap(prev[roundKey].bets || {});
        const gid = String(gameId);
        const nextBets = { ...bets };
        const previous = findHourglassEntry(nextBets);
        if (previous && !sameGameId(previous.gameId, gid)) {
          const src = nextBets[previous.gameId];
          if (src) {
            const { augment: _removed, hourglassPoints: _hv, ...rest } = src;
            nextBets[previous.gameId] = rest;
          }
        }
        const existing = nextBets[gid] || { predictedHomeScore: 0, predictedAwayScore: 0 };
        nextBets[gid] = { ...existing, augment: placedRef, hourglassPoints };
        return {
          ...prev,
          [roundKey]: {
            ...prev[roundKey],
            bets: nextBets
          }
        };
      });
      setSuccess(existingHourglass ? 'Hourglass moved.' : 'Hourglass placed.');
      setTimeout(() => setSuccess(''), BETTING_SUCCESS_TOAST_MS);
      const meta = getAugmentByValue(HOURGLASS_AUGMENT_CODE);
      const displaySvg = getAugmentDisplaySvg(placedRef, user?.inventory) ?? meta?.svg ?? null;
      triggerMatchAugmentSpreadWave([gameId], meta?.color || 'gold', displaySvg);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to place Hourglass');
    } finally {
      setSubmitting(false);
      setDraggingAugment(null);
      setDraggingFromGameId(null);
    }
  };

  const handleAugmentRemove = (gameId) => {
    const selectedRound = getSelectedRoundData();
    const placedRef = selectedRound?.bets?.[String(gameId)]?.augment ?? null;
    if (placedRef) {
      restorePoolStylePreferenceFromPlacedRef(placedRef);
      setPoolStyleTick((n) => n + 1);
    }
    const code = placedRef ? getAugmentBaseCode(placedRef) : null;
    if (code === 'd' || code === 'm' || code === BOUNTY_AUGMENT_CODE) {
      const roundKey =
        selectedRoundType === 'previous'
          ? 'previousRound'
          : selectedRoundType === 'current'
            ? 'currentRound'
            : 'nextRound';
      setError('');
      setRoundsData((prev) => {
        if (!prev?.[roundKey]) return prev;
        return {
          ...prev,
          [roundKey]: {
            ...prev[roundKey],
            bets: stripSacrificeAugmentsFromBets(normalizeBetsMap(prev[roundKey].bets || {}))
          }
        };
      });
      return;
    }
    handleAugmentPlace(gameId, null);
  };

  applyMatchTouchDropRef.current = (point) => {
    const dragData = touchDragDataRef.current;
    if (!dragData) return;
    touchDragDataRef.current = null;
    if (!point) {
      resetMatchAugmentTouchDragState();
      return;
    }
    const element = elementUnderTouch(point.x, point.y);
    const action = resolveMatchTouchDrop({
      point,
      dragData,
      element,
      overMatchPlays: isPointOverMatchPlays(point)
    });
    if (action.type === 'remove') {
      handleAugmentRemove(action.gameId);
    } else if (action.type === 'drop') {
      handleAugmentDrop(
        action.gameId,
        dragData,
        getDropSideFromPointerEvent({ clientX: point.x, target: element }, action.element),
        action.element
      );
    }
    resetMatchAugmentTouchDragState();
  };

  const resolveRoundAugmentRef = useCallback(
    (baseCode, inventory, targets) => {
      if (!baseCode) return null;
      const styleId = getMatchPoolStyleId(baseCode);
      const owned = getOwnedStyleIdsForPoolAugment(baseCode, inventory);
      const resolvedStyle =
        styleId != null && owned.includes(styleId) ? styleId : null;
      return formatAugmentRef(baseCode, resolvedStyle, targets);
    },
    [getMatchPoolStyleId]
  );

  const handleRoundAugmentChange = (value) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.roundSubmissionOpen) return;
    const roundKey =
      selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    const inventory = user?.inventory ?? {};
    const slotPickerCode =
      value === 'aon' || value === 'sn' ? value : null;
    const prevTargets =
      slotPickerCode && getAugmentBaseCode(selectedRound.roundAugment ?? '') === slotPickerCode
        ? parseAugmentRef(selectedRound.roundAugment).targets
        : [];
    const next =
      value === '' || value == null
        ? null
        : resolveRoundAugmentRef(value, inventory, slotPickerCode ? prevTargets : undefined);
    const matchTp = totalTacticalPointsForOpenGamesBets(selectedRound);
    if (matchTp + getRoundAugmentTacticalPointsCost(next) > getTacticalPointsCapForRound(selectedLeague?.code, selectedRound)) {
      const cap = getTacticalPointsCapForRound(selectedLeague?.code, selectedRound);
      setError(
        `Not enough tactical points (${cap} max). This round play costs ${getRoundAugmentTacticalPointsCost(next)}.`
      );
      return;
    }
    setError('');
    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      return {
        ...prev,
        [roundKey]: { ...prev[roundKey], roundAugment: next }
      };
    });
    if (next) {
      const color = DEFAULT_ROUND_AUGMENTS.find((x) => x.value === getAugmentBaseCode(next))?.color || 'gold';
      triggerRoundAugmentSpreadWave(color);
    }
  };

  const handleRoundAugmentStyleChange = (baseCode, styleId) => {
    selectMatchPoolStyle(baseCode, styleId);
  };

  const handleRoundAugmentSlotToggle = (gameId) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.roundSubmissionOpen) return;
    const game = selectedRound.games?.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;
    const code = getAugmentBaseCode(selectedRound.roundAugment ?? '');
    if (code !== 'aon' && code !== 'sn') return;
    const slot = gameSlotFromId(gameId);
    if (slot == null) return;
    const maxSlots = code === 'aon' ? ALL_OR_NOTHING_MAX_SLOTS : SAFETY_NET_MAX_SLOTS;
    const label = code === 'aon' ? 'All / nothing' : 'Safety net';
    const roundKey =
      selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    const parsed = parseAugmentRef(selectedRound.roundAugment);
    const has = parsed.targets.includes(slot);
    let nextTargets;
    if (has) {
      nextTargets = parsed.targets.filter((s) => s !== slot);
    } else {
      const deadEntry = findSacrificeDeadEntry(selectedRound.bets);
      if (deadEntry && sameGameId(deadEntry.gameId, gameId)) {
        setError(`${label} cannot include a sacrificed match.`);
        return;
      }
      if (parsed.targets.length >= maxSlots) {
        setError(`${label} allows at most ${maxSlots} matches.`);
        return;
      }
      nextTargets = [...parsed.targets, slot].sort((a, b) => a - b);
    }
    setError('');
    const inventory = user?.inventory ?? {};
    const next = resolveRoundAugmentRef(code, inventory, nextTargets);
    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      return {
        ...prev,
        [roundKey]: { ...prev[roundKey], roundAugment: next }
      };
    });
  };

  const handleMatchVoteToggle = (gameId) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.roundSubmissionOpen) return;
    const game = selectedRound.games?.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;
    const gid = String(gameId);
    const roundKey =
      selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    const current = Array.isArray(selectedRound.votes) ? selectedRound.votes.map(String) : [];
    const has = current.includes(gid);
    let next;
    if (has) {
      next = current.filter((id) => id !== gid);
    } else {
      const deadEntry = findSacrificeDeadEntry(selectedRound.bets);
      if (deadEntry && sameGameId(deadEntry.gameId, gameId)) {
        setError('Sacrificed matches cannot receive a vote.');
        return;
      }
      if (current.length >= MAX_MATCH_VOTES) {
        setError(`You may vote for at most ${MAX_MATCH_VOTES} matches.`);
        return;
      }
      next = [...current, gid];
    }
    setError('');
    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      return {
        ...prev,
        [roundKey]: { ...prev[roundKey], votes: next }
      };
    });
  };

  const handleAugmentPlace = (gameId, augmentValue) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound || !selectedRound.roundSubmissionOpen) return;
    const game = selectedRound.games.find((g) => sameGameId(g.gameId, gameId));
    const clearing = augmentValue == null || augmentValue === '';
    if (!game) return;
    if (!isGameOnRoundCard(game) && !(clearing && canRemoveAugmentFromGame(game))) return;

    const tpCap = getTacticalPointsCapForRound(selectedLeague?.code, selectedRound);
    const placingCode = augmentValue ? getAugmentBaseCode(augmentValue) : null;
    const existingCode = selectedRound.bets?.[String(gameId)]?.augment
      ? getAugmentBaseCode(selectedRound.bets[String(gameId)].augment)
      : null;
    const deadEntry = findSacrificeDeadEntry(selectedRound.bets);

    if (placingCode === 'm' || placingCode === BOUNTY_AUGMENT_CODE) {
      const followLabel = placingCode === BOUNTY_AUGMENT_CODE ? 'Bounty' : 'Multiplier';
      if (!deadEntry) {
        setError(`Place Sacrifice on a match first, then drag ${followLabel} onto another match.`);
        return;
      }
      if (sameGameId(deadEntry.gameId, gameId)) {
        setError(`${followLabel} must go on a different match than Sacrifice.`);
        return;
      }
      if (placingCode === BOUNTY_AUGMENT_CODE) {
        const available = availableBountyStakeForRound(
          selectedRound,
          roundsDataRef.current?.seasonStack
        );
        if (available < BOUNTY_STAKE_MIN) {
          setError(`Bounty needs at least ${BOUNTY_STAKE_MIN} Stack.`);
          return;
        }
      }
    }

    if (placingCode === PARLAY_AUGMENT_CODE) {
      const parlayCount = countParlayPlacements(selectedRound.bets);
      const alreadyOnTarget = existingCode === PARLAY_AUGMENT_CODE;
      const otherCount = alreadyOnTarget ? parlayCount - 1 : parlayCount;
      if (otherCount >= PARLAY_MAX_USES) {
        setError('Parlay links exactly two matches. Remove one first.');
        return;
      }
      if (otherCount === 1) {
        const otherGid = Object.keys(selectedRound.bets || {}).find(
          (gid) =>
            !sameGameId(gid, gameId) &&
            selectedRound.bets[gid]?.augment &&
            getAugmentBaseCode(selectedRound.bets[gid].augment) === PARLAY_AUGMENT_CODE
        );
        if (otherGid && sameGameId(otherGid, gameId)) {
          setError('Parlay partner must be a different match.');
          return;
        }
      }
    }

    if (placingCode === HOURGLASS_AUGMENT_CODE) {
      if (deadEntry && sameGameId(deadEntry.gameId, gameId)) {
        setError('Hourglass cannot go on the sacrificed match.');
        return;
      }
      if (!isHourglassPlacementOpenForGame(game)) {
        setError('Hourglass must be placed before the second half starts on this match.');
        return;
      }
    }

    if (augmentValue) {
      const code = placingCode;
      if (isKnockoutOnlyAugmentCode(code) && !isKnockoutMatchGame(game)) {
        setError('Extra time and shootout plays are only for knockout matches.');
        return;
      }
      const teamSideGroup = getTeamSideAugmentGroup(code);
      if (teamSideGroup) {
        let uses = 0;
        for (const g of selectedRound.games || []) {
          const gid = String(g.gameId);
          if (sameGameId(gid, gameId)) continue;
          const placed = selectedRound.bets?.[gid]?.augment;
          if (!placed) continue;
          const placedBase = getAugmentBaseCode(placed);
          if (teamSideGroup.variants.includes(placedBase)) uses += 1;
        }
        if (uses >= 2) {
          setError(
            `${teamSideGroup.uiAugment.displayName} can be used at most twice per round (either side).`
          );
          return;
        }
      }
      if ((code === 'ph' || code === 'pa') && isShootoutUiDrag(augmentValue)) {
        const bet = selectedRound.bets?.[String(gameId)];
        const h = bet?.predictedHomeScore ?? 0;
        const a = bet?.predictedAwayScore ?? 0;
        if (h !== a) {
          setError('Shootout plays require a draw prediction.');
          return;
        }
      }
      const afterMatchTp = totalTacticalPointsAfterPlacingAugment(selectedRound, gameId, augmentValue);
      const roundAug = selectedRound.roundAugment ?? null;
      const afterTp = afterMatchTp + getRoundAugmentTacticalPointsCost(roundAug);
      if (afterTp > tpCap) {
        setError(
          `Not enough tactical points (${tpCap} max this round). This would use ${afterTp} tactical points—remove plays or pick a lighter one.`
        );
        return;
      }
    }

    const existingBet = selectedRound.bets?.[String(gameId)];
    const roundKey = selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    setError('');
    setSelectedAugment(null);
    setDraggingAugment(null);
    setDraggingFromGameId(null);

    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      let newBets = normalizeBetsMap(prev[roundKey].bets || {});
      const gidTarget = String(gameId);

      if (placingCode === 'm' || placingCode === BOUNTY_AUGMENT_CODE) {
        const dead = findSacrificeDeadEntry(newBets);
        if (!dead) return prev;
        const beneficiarySlot = gameSlotFromId(gameId);
        if (beneficiarySlot == null) return prev;

        newBets = stripSacrificeFollowUpFromBets(newBets);

        const deadParsed = parseAugmentRef(dead.augment);
        const deadRef = formatAugmentRef('d', deadParsed.styleId, [beneficiarySlot]);
        const followParsed = parseAugmentRef(String(augmentValue));
        const followRef = formatAugmentRef(placingCode, followParsed.styleId);
        const existing = newBets[gidTarget];
        const nextBet = {
          predictedHomeScore: existing?.predictedHomeScore ?? 0,
          predictedAwayScore: existing?.predictedAwayScore ?? 0,
          ...existing,
          augment: followRef
        };
        if (placingCode === BOUNTY_AUGMENT_CODE) {
          const available = availableBountyStakeForRound(
            prev[roundKey],
            prev.seasonStack
          );
          nextBet.bountyPoints = clampBountyStake(
            existing?.bountyPoints,
            available
          );
        } else {
          delete nextBet.bountyPoints;
        }
        newBets = {
          ...newBets,
          [dead.gameId]: {
            ...newBets[dead.gameId],
            augment: deadRef
          },
          [gidTarget]: nextBet
        };
        return { ...prev, [roundKey]: { ...prev[roundKey], bets: newBets } };
      }

      if (
        placingCode === 'd' ||
        existingCode === 'd' ||
        existingCode === 'm' ||
        existingCode === BOUNTY_AUGMENT_CODE
      ) {
        newBets = stripSacrificeAugmentsFromBets(newBets);
      }

      if (placingCode === PARLAY_AUGMENT_CODE) {
        const partnerSlot = gameSlotFromId(gameId);
        if (partnerSlot == null) return prev;
        const pParsed = parseAugmentRef(String(augmentValue));
        const otherGid = Object.keys(newBets).find(
          (gid) =>
            String(gid) !== gidTarget &&
            newBets[gid]?.augment &&
            getAugmentBaseCode(newBets[gid].augment) === PARLAY_AUGMENT_CODE
        );
        const existing = newBets[gidTarget];
        if (!otherGid) {
          newBets = {
            ...newBets,
            [gidTarget]: {
              ...existing,
              predictedHomeScore: existing?.predictedHomeScore ?? 0,
              predictedAwayScore: existing?.predictedAwayScore ?? 0,
              augment: formatAugmentRef(PARLAY_AUGMENT_CODE, pParsed.styleId, [])
            }
          };
          return { ...prev, [roundKey]: { ...prev[roundKey], bets: newBets } };
        }
        const otherParsed = parseAugmentRef(String(newBets[otherGid].augment));
        const anchorRef = formatAugmentRef(PARLAY_AUGMENT_CODE, otherParsed.styleId, [partnerSlot]);
        const partnerRef = formatAugmentRef(PARLAY_AUGMENT_CODE, pParsed.styleId, []);
        newBets = {
          ...newBets,
          [otherGid]: {
            ...newBets[otherGid],
            augment: anchorRef
          },
          [gidTarget]: {
            ...existing,
            predictedHomeScore: existing?.predictedHomeScore ?? 0,
            predictedAwayScore: existing?.predictedAwayScore ?? 0,
            ...(existing?.clashAugments ? { clashAugments: existing.clashAugments } : {}),
            augment: partnerRef
          }
        };
        return { ...prev, [roundKey]: { ...prev[roundKey], bets: newBets } };
      }

      if (!placingCode && existingCode === PARLAY_AUGMENT_CODE) {
        newBets = stripParlayAugmentsFromBets(newBets);
        const existing = newBets[gidTarget];
        const newBet = {
          ...existing,
          predictedHomeScore: existing?.predictedHomeScore ?? 0,
          predictedAwayScore: existing?.predictedAwayScore ?? 0
        };
        delete newBet.augment;
        return {
          ...prev,
          [roundKey]: { ...prev[roundKey], bets: { ...newBets, [gidTarget]: newBet } }
        };
      }

      if (placingCode === HOURGLASS_AUGMENT_CODE) {
        for (const gid of Object.keys(newBets)) {
          if (newBets[gid]?.augment && getAugmentBaseCode(newBets[gid].augment) === HOURGLASS_AUGMENT_CODE) {
            const b = { ...newBets[gid] };
            delete b.augment;
            delete b.hourglassPoints;
            newBets = { ...newBets, [gid]: b };
          }
        }
      } else if (placingCode && placingCode !== PARLAY_AUGMENT_CODE) {
        for (const gid of Object.keys(newBets)) {
          if (String(gid) === gidTarget) continue;
          if (newBets[gid]?.augment && getAugmentBaseCode(newBets[gid].augment) === placingCode) {
            const b = { ...newBets[gid] };
            delete b.augment;
            newBets = { ...newBets, [gid]: b };
          }
        }
      }
      const existing = newBets[gidTarget];
      const newBet = {
        ...existing,
        predictedHomeScore: existing?.predictedHomeScore ?? 0,
        predictedAwayScore: existing?.predictedAwayScore ?? 0
      };
      if (augmentValue) {
        let placed = augmentValue;
        if (getAugmentBaseCode(placed) === 's') {
          placed = ensureSplitAugmentRef(
            placed,
            newBet.predictedHomeScore,
            newBet.predictedAwayScore
          );
        }
        if (getAugmentBaseCode(placed) === 'd') {
          const parsed = parseAugmentRef(placed);
          placed = formatAugmentRef('d', parsed.styleId, []);
        }
        newBet.augment = placed;
        if (getAugmentBaseCode(placed) !== BOUNTY_AUGMENT_CODE) {
          delete newBet.bountyPoints;
        }
      } else {
        delete newBet.augment;
        delete newBet.hourglassPoints;
        delete newBet.bountyPoints;
      }
      const nextRound = {
        ...prev[roundKey],
        bets: { ...newBets, [gidTarget]: newBet }
      };
      if (placingCode === 'd') {
        const cleared = clearRoundSelectionsForSacrificedMatch({
          votes: prev[roundKey].votes,
          roundAugment: prev[roundKey].roundAugment,
          gameId: gidTarget,
          gameSlot: gameSlotFromId(gidTarget)
        });
        nextRound.votes = cleared.votes;
        nextRound.roundAugment = cleared.roundAugment;
      }
      return {
        ...prev,
        [roundKey]: nextRound
      };
    });

    if (augmentValue) {
      const meta =
        getAugmentByValue(augmentValue) ?? getAugmentByValue(getAugmentBaseCode(augmentValue));
      const displaySvg =
        getAugmentDisplaySvg(augmentValue, user?.inventory) ?? meta?.svg ?? null;
      triggerMatchAugmentSpreadWave([gameId], meta?.color || 'gold', displaySvg);
    }
  };

  const handleClashAugmentPick = (gameId, augmentMapKey, rawValue) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.roundSubmissionOpen) return;
    const game = selectedRound.games.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;

    const roundKey =
      selectedRoundType === 'previous' ? 'previousRound' : selectedRoundType === 'current' ? 'currentRound' : 'nextRound';
    const code = rawValue === '' || rawValue == null ? null : String(rawValue);

    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      const round = prev[roundKey];
      const gidStr = String(gameId);
      let bets = normalizeBetsMap(round.bets || {});

      if (code) {
        for (const gid of Object.keys(bets)) {
          if (String(gid) === gidStr) continue;
          const ob = bets[gid];
          if (!ob?.clashAugments || typeof ob.clashAugments !== 'object') continue;
          if (!Object.prototype.hasOwnProperty.call(ob.clashAugments, augmentMapKey)) continue;
          // Only clear from another match when moving the **same** play code (one use per type per opponent).
          if (getAugmentBaseCode(ob.clashAugments[augmentMapKey]) !== getAugmentBaseCode(code)) continue;
          const copy = { ...ob.clashAugments };
          delete copy[augmentMapKey];
          const nb = { ...ob };
          if (Object.keys(copy).length > 0) nb.clashAugments = copy;
          else delete nb.clashAugments;
          bets = { ...bets, [gid]: nb };
        }
      }

      const existing = bets[gidStr] ?? {
        predictedHomeScore: 0,
        predictedAwayScore: 0
      };
      const nextCa = {
        ...(existing.clashAugments && typeof existing.clashAugments === 'object' ? existing.clashAugments : {})
      };
      if (code) nextCa[augmentMapKey] = code;
      else delete nextCa[augmentMapKey];

      const nextBet = {
        ...existing,
        predictedHomeScore: existing.predictedHomeScore ?? 0,
        predictedAwayScore: existing.predictedAwayScore ?? 0
      };
      if (Object.keys(nextCa).length > 0) nextBet.clashAugments = nextCa;
      else delete nextBet.clashAugments;

      const nextRound = {
        ...round,
        bets: { ...bets, [gidStr]: nextBet }
      };
      const maxPerClash = maxClashTacticalSpendPerClashForOpenGames(nextRound);
      if (maxPerClash > CLASH_TACTICAL_POINTS_MAX_PER_CLASH) {
        setTimeout(() => {
          setError(
            `Clash tactical points limit (${CLASH_TACTICAL_POINTS_MAX_PER_CLASH} max per opponent). Pick a cheaper clash play for that clash or clear one.`
          );
        }, 0);
        return prev;
      }
      setTimeout(() => setError(''), 0);
      return { ...prev, [roundKey]: nextRound };
    });
  };

  const handleSplitSecondChange = (gameId, nextHome, nextAway, nextWeight) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound || !selectedRound.roundSubmissionOpen) return;
    const game = selectedRound.games.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;

    const roundKey =
      selectedRoundType === 'previous'
        ? 'previousRound'
        : selectedRoundType === 'current'
          ? 'currentRound'
          : 'nextRound';
    const gidKey = String(gameId);

    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      const bets = normalizeBetsMap(prev[roundKey].bets || {});
      const cur = bets[gidKey];
      if (!cur?.augment || getAugmentBaseCode(cur.augment) !== 's') return prev;
      const parsed = parseAugmentRef(cur.augment);
      const ph = cur.predictedHomeScore ?? 0;
      const pa = cur.predictedAwayScore ?? 0;
      let home = clampScore(nextHome);
      let away = clampScore(nextAway);
      let weight = Number(nextWeight);
      if (!Number.isInteger(weight)) weight = parsed.split?.weight ?? SPLIT_SECOND_WEIGHT_MAX;
      weight = Math.min(SPLIT_SECOND_WEIGHT_MAX, Math.max(SPLIT_SECOND_WEIGHT_MIN, weight));
      if (!splitHasDistinctScoreline(ph, pa, { home, away })) {
        const next = defaultSplitForPrimary(ph, pa);
        home = next.home;
        away = next.away;
      }
      const nextRef = formatSplitAugmentRef('s', parsed.styleId, { home, away, weight });
      return {
        ...prev,
        [roundKey]: {
          ...prev[roundKey],
          bets: { ...bets, [gidKey]: { ...cur, augment: nextRef } }
        }
      };
    });
  };

  const handleBountyStakeChange = (gameId, nextStake) => {
    const selectedRound = getSelectedRoundData();
    if (!selectedRound || !selectedRound.roundSubmissionOpen) return;
    const game = selectedRound.games.find((g) => sameGameId(g.gameId, gameId));
    if (!game || !isGameOnRoundCard(game)) return;

    const roundKey =
      selectedRoundType === 'previous'
        ? 'previousRound'
        : selectedRoundType === 'current'
          ? 'currentRound'
          : 'nextRound';
    const gidKey = String(gameId);
    const available = availableBountyStakeForRound(selectedRound, roundsData?.seasonStack);
    const stake = clampBountyStake(nextStake, available);
    if (stake < BOUNTY_STAKE_MIN) return;

    setRoundsData((prev) => {
      if (!prev?.[roundKey]) return prev;
      const bets = normalizeBetsMap(prev[roundKey].bets || {});
      const cur = bets[gidKey];
      if (!cur?.augment || getAugmentBaseCode(cur.augment) !== BOUNTY_AUGMENT_CODE) return prev;
      return {
        ...prev,
        [roundKey]: {
          ...prev[roundKey],
          bets: { ...bets, [gidKey]: { ...cur, bountyPoints: stake } }
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    const selectedRound = getSelectedRoundData();
    if (!selectedRound?.partitionKey) {
      setError('No round selected');
      setSubmitting(false);
      return;
    }

    if (!selectedRound.roundSubmissionOpen) {
      setError('This round is locked — picks must be saved before the first kickoff.');
      setSubmitting(false);
      return;
    }

    const betsArray = selectedRound.games
      .filter((game) => isGameOnRoundCard(game))
      .map((game) => {
        const b = selectedRound.bets?.[String(game.gameId)] ?? selectedRound.bets?.[game.gameId];
        const bet = {
          gameId: game.gameId,
          predictedHomeScore: clampScore(b?.predictedHomeScore),
          predictedAwayScore: clampScore(b?.predictedAwayScore)
        };
        if (b?.augment) bet.augment = b.augment;
        if (
          b?.augment &&
          getAugmentBaseCode(b.augment) === BOUNTY_AUGMENT_CODE &&
          b.bountyPoints != null
        ) {
          bet.bountyPoints = Number(b.bountyPoints);
        }
        if (b?.clashAugments != null && typeof b.clashAugments === 'object' && Object.keys(b.clashAugments).length > 0) {
          bet.clashAugments = b.clashAugments;
        }
        return bet;
      });

    const augmentUsed = {};
    const teamSideGroupCounts = {};
    let parlayCount = 0;
    for (const b of betsArray) {
      if (!b.augment) continue;
      const base = getAugmentBaseCode(b.augment);
      const teamSideGroup = getTeamSideAugmentGroup(base);
      if (teamSideGroup) {
        const key = teamSideGroup.uiValue;
        teamSideGroupCounts[key] = (teamSideGroupCounts[key] || 0) + 1;
        if (teamSideGroupCounts[key] > 2) {
          setError(
            `${teamSideGroup.uiAugment.displayName} can be used at most twice per round (either side).`
          );
          setSubmitting(false);
          return;
        }
        continue;
      }
      if (base === PARLAY_AUGMENT_CODE) {
        parlayCount += 1;
        if (parlayCount > PARLAY_MAX_USES) {
          setError('Parlay can link at most two matches per round.');
          setSubmitting(false);
          return;
        }
        continue;
      }
      if (augmentUsed[base]) {
        setError(`Each play can only be used once per round. You used ${getAugmentDisplayName(base)} twice.`);
        setSubmitting(false);
        return;
      }
      augmentUsed[base] = true;
      if (base === 's') {
        const { split } = parseAugmentRef(b.augment);
        if (!isValidSplitPayload(split)) {
          setError('Split play needs a second scoreline with 1–5 points.');
          setSubmitting(false);
          return;
        }
        if (!splitHasDistinctScoreline(b.predictedHomeScore, b.predictedAwayScore, split)) {
          setError(
            'Split second scoreline must differ from your primary prediction.'
          );
          setSubmitting(false);
          return;
        }
      }
    }

    if (augmentUsed.d || augmentUsed.m || augmentUsed[BOUNTY_AUGMENT_CODE]) {
      if (!isSacrificePairComplete(selectedRound.bets)) {
        setError(
          'Sacrifice needs a dead match and Multiplier or Bounty on a different match. Drag one onto another match to finish.'
        );
        setSubmitting(false);
        return;
      }
      const bountyEntry = findBountyEntry(selectedRound.bets);
      if (bountyEntry) {
        const stake = resolveBountyPoints(
          selectedRound.bets[bountyEntry.gameId]?.bountyPoints
        );
        const available = availableBountyStakeForRound(
          selectedRound,
          roundsData?.seasonStack
        );
        if (stake < BOUNTY_STAKE_MIN || stake > Math.min(BOUNTY_STAKE_MAX, available)) {
          setError(
            `Bounty stake must be ${BOUNTY_STAKE_MIN}–${Math.min(BOUNTY_STAKE_MAX, available)} from your Stack.`
          );
          setSubmitting(false);
          return;
        }
      }
    }

    if (parlayCount > 0 && !isParlayPairComplete(selectedRound.bets)) {
      setError('Parlay needs two linked matches. Drag Parlay onto a second match to finish.');
      setSubmitting(false);
      return;
    }

    const clashAugmentSlotUsed = new Set();
    for (const b of betsArray) {
      const ca = b.clashAugments;
      if (!ca || typeof ca !== 'object') continue;
      for (const [k, v] of Object.entries(ca)) {
        if (v == null || v === '') continue;
        const slot = `${k}::${getAugmentBaseCode(v)}`;
        if (clashAugmentSlotUsed.has(slot)) {
          setError(
            'Each clash play type can only be used once per opponent per round — that play is already on another match.'
          );
          setSubmitting(false);
          return;
        }
        clashAugmentSlotUsed.add(slot);
      }
    }

    const tpTotal = totalMatchAndRoundTacticalPoints(selectedRound, selectedRound.roundAugment ?? null);
    if (tpTotal > tacticalPointsCap) {
      setError(`Tactical points limit exceeded (${tpTotal}/${tacticalPointsCap}). Remove or swap plays.`);
      setSubmitting(false);
      return;
    }

    for (const [clashKey, spent] of clashTacticalPointsByKeyForOpenGames(selectedRound)) {
      if (spent > CLASH_TACTICAL_POINTS_MAX_PER_CLASH) {
        setError(
          `Clash tactical points exceed ${CLASH_TACTICAL_POINTS_MAX_PER_CLASH} for one opponent (${spent} on "${clashKey}"). Remove or swap plays for that clash.`
        );
        setSubmitting(false);
        return;
      }
    }

    if (betsArray.length === 0) {
      setError('No matches available to pick in this round.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        partitionKey: selectedRound.partitionKey,
        leagueCode: selectedLeague.code,
        leagueId: selectedLeague.code,
        bets: betsArray
      };
      const ra = selectedRound.roundAugment;
      if (ra) payload.roundAugment = ra;
      const votes = (Array.isArray(selectedRound.votes) ? selectedRound.votes.map(String) : []).filter((gid) =>
        selectedRound.games.some((g) => sameGameId(g.gameId, gid) && isGameOnRoundCard(g))
      );
      if (votes.length > 0) payload.votes = votes;
      const { data } = await api.post('/picks', payload);
      if (data.token) {
        updateToken(data.token, { tieredLeagues: data.tieredLeagues });
      }
      const tierClash =
        data.tierClash &&
        typeof data.tierClash === 'object' &&
        data.tierClash.opponentUsername &&
        data.tierClash.augmentMapKey
          ? data.tierClash
          : null;
      setSuccess(tierClash ? TIER_CLASH_SAVE_MESSAGE : 'Your picks have been saved.');
      setError('');
      // Saving open games replaces the round card in Dynamo and drops postponed slots —
      // clear reclaimable plays locally so the pool matches the server.
      const stripPostponedAugments = (betsMap, games) => {
        const next = { ...normalizeBetsMap(betsMap || {}) };
        for (const g of games || []) {
          if (!canRemoveAugmentFromGame(g)) continue;
          const gid = String(g.gameId);
          if (!next[gid]?.augment && !next[gid]?.clashAugments) continue;
          const slot = { ...next[gid] };
          delete slot.augment;
          delete slot.clashAugments;
          next[gid] = slot;
        }
        return next;
      };
      const savedRoundKey = roundTypeToDataKey(selectedRoundType);
      const betsAfterSave = stripPostponedAugments(selectedRound.bets, selectedRound.games);
      const snapshotKey = roundSnapshotKey(
        selectedRound,
        selectedLeague.code,
        savedRoundKey
      );
      if (snapshotKey) {
        setSavedSnapshots((prev) => ({
          ...prev,
          [snapshotKey]: serializeRoundBetsSnapshot({
            ...selectedRound,
            bets: betsAfterSave
          })
        }));
      }
      setRoundsData((prev) => {
        const round = prev?.[savedRoundKey];
        if (!round) return prev;
        const bets = stripPostponedAugments(round.bets, round.games);
        const serverBets = stripPostponedAugments(round.serverBets ?? {}, round.games);
        for (const b of betsArray) {
          const gid = String(b.gameId);
          serverBets[gid] = {
            predictedHomeScore: b.predictedHomeScore,
            predictedAwayScore: b.predictedAwayScore,
            ...(b.augment && { augment: b.augment }),
            ...(b.bountyPoints != null && { bountyPoints: b.bountyPoints }),
            ...(b.clashAugments && { clashAugments: { ...b.clashAugments } })
          };
        }
        let clashTargets = Array.isArray(round.clashTargets) ? [...round.clashTargets] : [];
        if (tierClash) {
          const already = clashTargets.some((t) => t?.augmentMapKey === tierClash.augmentMapKey);
          if (!already) {
            clashTargets = [
              ...clashTargets,
              {
                leagueId: tierClash.leagueId,
                ...(tierClash.leagueType ? { leagueType: tierClash.leagueType } : {}),
                leagueName: tierClash.leagueId,
                opponentUsername: tierClash.opponentUsername,
                augmentMapKey: tierClash.augmentMapKey
              }
            ];
          }
        }
        return {
          ...prev,
          [savedRoundKey]: {
            ...round,
            bets,
            serverBets,
            ...(tierClash ? { clashTargets } : {})
          },
          ...(data.seasonStack != null ? { seasonStack: Number(data.seasonStack) } : {})
        };
      });
      setTimeout(
        () => setSuccess(''),
        tierClash ? BETTING_TIER_CLASH_TOAST_MS : BETTING_SUCCESS_TOAST_MS
      );
    } catch (err) {
      if (err.response?.status === 401) return; // Handled by api interceptor (logout)
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save picks');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRound = getSelectedRoundData();
  const tacticalPointsCap = getTacticalPointsCapForRound(selectedLeague?.code, selectedRound);
  const editedRoundKey = roundTypeToDataKey(selectedRoundType);
  const editedRound = roundsData?.[editedRoundKey] ?? null;
  const hasUnsavedChanges =
    editedRound != null && selectedLeague
      ? hasUnsavedRoundChanges(editedRound, savedSnapshots, selectedLeague.code, editedRoundKey)
      : false;

  if (!selectedLeague || loading) {
    return <BettingPageLoading />;
  }

  const leagueRoundsReady = loadedLeagueCode === selectedLeague.code;
  const hasRounds = Boolean(
    leagueRoundsReady &&
      roundsData &&
      (roundsData.previousRound || roundsData.currentRound || roundsData.nextRound)
  );
  const showNoRounds = shouldShowNoRoundsAvailable({
    loading,
    hasRounds,
    loadedLeagueCode,
    selectedLeagueCode: selectedLeague.code
  });
  const isRoundFinished = selectedRound?.games?.length > 0 && selectedRound.games.every((g) => !isGameOnRoundCard(g));
  const isRoundLocked = !!selectedRound && !isRoundFinished && !selectedRound.roundSubmissionOpen;
  /** Hide play pickers and tactical meter when the round is over or picks are locked. */
  const showAugmentControls = !!(selectedRound?.roundSubmissionOpen && !isRoundFinished);
  const seasonStack = Number(roundsData?.seasonStack) || 0;
  const selectedRoundAugmentCode = selectedRound?.roundAugment ?? null;
  const slotPickerCode = getAugmentBaseCode(selectedRoundAugmentCode ?? '');
  const slotPickerSelected = slotPickerCode === 'aon' || slotPickerCode === 'sn';
  const slotPickerTargets = slotPickerSelected
    ? parseAugmentRef(selectedRoundAugmentCode).targets
    : [];
  const slotPickerTargetSet = new Set(slotPickerTargets);
  const slotPickerMax =
    slotPickerCode === 'aon'
      ? ALL_OR_NOTHING_MAX_SLOTS
      : slotPickerCode === 'sn'
        ? SAFETY_NET_MAX_SLOTS
        : 0;
  const slotPickerLabel = slotPickerCode === 'aon' ? 'All / nothing' : 'Safety net';
  const aonPlayingForPts =
    slotPickerCode === 'aon' ? allOrNothingPointsForSlotCount(slotPickerTargets.length) : 0;
  const slotPickerHint =
    slotPickerCode === 'aon'
      ? `Playing for ${aonPlayingForPts} pts so far.`
      : 'Wrong outcomes pay 5→4→3→2→1.';
  const slotPickerCss = slotPickerCode === 'aon' ? 'aon' : 'safety-net';
  const slotPickerIconSvg = slotPickerSelected
    ? getAugmentDisplaySvg(selectedRoundAugmentCode, user?.inventory) ??
      DEFAULT_ROUND_AUGMENTS.find((ra) => ra.value === slotPickerCode)?.svg
    : null;
  const matchVotes = Array.isArray(selectedRound?.votes) ? selectedRound.votes.map(String) : [];
  const sacrificeDeadEntry = findSacrificeDeadEntry(selectedRound?.bets);
  const hourglassEntry = findHourglassEntry(selectedRound?.bets);
  const hourglassSourceGame = hourglassEntry
    ? selectedRound?.games?.find((g) => sameGameId(g.gameId, hourglassEntry.gameId))
    : null;
  const hourglassSourceMovable =
    !!hourglassSourceGame &&
    isHourglassPlacementOpenForGame(hourglassSourceGame) &&
    canMoveHourglass(hourglassEntry?.hourglassPoints);
  const isHourglassTargetGame = (game) => {
    if (!isRoundLocked || !game) return false;
    if (!isGameOnRoundCard(game)) return false;
    if (!isHourglassPlacementOpenForGame(game)) return false;
    if (sacrificeDeadEntry && sameGameId(sacrificeDeadEntry.gameId, game.gameId)) return false;
    const aug = selectedRound?.bets?.[String(game.gameId)]?.augment;
    if (aug) return false;
    if (hourglassEntry && !hourglassSourceMovable) return false;
    return true;
  };
  const parlayPlacementCount = countParlayPlacements(selectedRound?.bets || {});
  const parlayPairComplete = isParlayPairComplete(selectedRound?.bets || {});
  const showReadonlyRoundAugment = !showAugmentControls && !!selectedRoundAugmentCode;
  const augmentUsedByGame = {};
  const placedAugmentBases = [];
  if (selectedRound?.games) {
    for (const g of selectedRound.games) {
      const a = selectedRound.bets?.[String(g.gameId)]?.augment;
      if (a) {
        placedAugmentBases.push(getAugmentBaseCode(a));
        augmentUsedByGame[a] = g.gameId;
        augmentUsedByGame[getAugmentBaseCode(a)] = g.gameId;
      }
    }
  }

  const draggingTeamSideGroup = getTeamSideAugmentGroup(draggingAugment);
  const isDraggingTeamSideAugment = !!draggingTeamSideGroup;
  const augmentCarouselMax = Math.max(0, playerAugments.length - augmentsPerView);
  const showAugmentCarousel = playerAugments.length > augmentsPerView;
  const roundAugmentOptionsCount = 1 + DEFAULT_ROUND_AUGMENTS.length;
  const roundAugmentCarouselMax = Math.max(0, roundAugmentOptionsCount - augmentsPerView);
  const showRoundAugmentCarousel = roundAugmentOptionsCount > augmentsPerView;
  const tacticalPointsUsed = selectedRound
    ? totalMatchAndRoundTacticalPoints(selectedRound, selectedRound.roundAugment ?? null)
    : 0;
  const tacticalPointsPct = Math.min(100, (tacticalPointsUsed / tacticalPointsCap) * 100);
  const clashSheetGame =
    clashSheet && selectedRound?.games
      ? selectedRound.games.find((g) => String(g.gameId) === String(clashSheet.gameId))
      : null;
  const clashSheetTacticalOnKey =
    clashSheet && selectedRound
      ? clashTacticalPointsByKeyForOpenGames(selectedRound).get(clashSheet.augmentMapKey) ?? 0
      : 0;
  const clashSheetLive =
    clashSheetGame &&
    (clashSheetGame.status === 'live' ||
      clashSheetGame.status === 'in_play' ||
      clashSheetGame.status === 'active');
  const clashSheetDisabled =
    !clashSheetGame ||
    !selectedRound?.roundSubmissionOpen ||
    !isGameOnRoundCard(clashSheetGame) ||
    clashSheetLive;
  const firstKickoffMs = firstMainSlateKickoffMs(selectedRound?.games || []);
  const roundLockMs = firstKickoffMs != null ? firstKickoffMs - ROUND_LOCK_BUFFER_MS : null;
  const picksLockRemainingMs = roundLockMs != null ? roundLockMs - nowMs : null;
  const picksLockDuration =
    picksLockRemainingMs != null ? formatPicksLockDuration(picksLockRemainingMs) : null;
  const picksLockExactLabel = roundLockMs != null ? formatPicksLockExactTime(roundLockMs) : null;
  const picksLockBufferMins = Math.round(ROUND_LOCK_BUFFER_MS / 60_000);
  const readonlyRoundAugmentBase = selectedRoundAugmentCode
    ? getAugmentBaseCode(selectedRoundAugmentCode)
    : null;
  const readonlyRoundAugmentDef =
    (readonlyRoundAugmentBase &&
      DEFAULT_ROUND_AUGMENTS.find((ra) => ra.value === readonlyRoundAugmentBase)) ||
    (selectedRoundAugmentCode ? getAugmentByValue(selectedRoundAugmentCode) : null);
  const readonlyRoundAugmentSvg =
    selectedRoundAugmentCode && readonlyRoundAugmentDef
      ? getAugmentDisplaySvg(selectedRoundAugmentCode, user?.inventory) ?? readonlyRoundAugmentDef.svg
      : null;
  const readonlyRoundAugmentColor = readonlyRoundAugmentDef?.color || 'gold';
  const displayGames = sortGamesForPicksDisplay(selectedRound?.games ?? []);
  const isCurrentRoundView = selectedRoundType === 'current';
  const nextMatchInfo =
    isCurrentRoundView && selectedRound?.games ? pickNextMatchForPicks(selectedRound.games) : null;
  const nextMatch = nextMatchInfo?.match ?? null;
  const nextMatchExtraLive = nextMatchInfo?.extraLiveCount ?? 0;
  const nextMatchLive = nextMatch ? isGameLive(nextMatch) : false;
  const nextMatchKickoffLabel =
    nextMatch?.gameDate != null
      ? new Date(nextMatch.gameDate).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      : null;
  const showPicksLockSubtitle =
    !isRoundFinished && picksLockDuration && selectedRound?.roundSubmissionOpen;
  const showLeagueNameSubtitle = !isCurrentRoundView;

  return (
    <div className="betting-page">
      <header className="betting-header">
        <h1 className="betting-title">Place Your Picks</h1>
        {nextMatch && (
          <p className="betting-next-match" data-testid="picks-next-match">
            <span className="betting-next-match__label">{nextMatchLive ? 'Live' : 'Next'}</span>
            <TeamName name={nextMatch.homeTeam} className="betting-next-match__team" />
            <span className="betting-next-match__vs">vs</span>
            <TeamName name={nextMatch.awayTeam} className="betting-next-match__team" />
            {nextMatchLive && nextMatchExtraLive > 0 && (
              <span className="betting-next-match__extra" aria-label={`${nextMatchExtraLive} more live`}>
                +{nextMatchExtraLive}
              </span>
            )}
            {!nextMatchLive && nextMatchKickoffLabel && (
              <time
                className="betting-next-match__when"
                dateTime={new Date(nextMatch.gameDate).toISOString()}
              >
                {nextMatchKickoffLabel}
              </time>
            )}
          </p>
        )}
        {(showPicksLockSubtitle || showLeagueNameSubtitle) && (
          <p className="betting-subtitle betting-subtitle--picks-lock">
            {showPicksLockSubtitle ? (
              <>
                <time dateTime={new Date(roundLockMs).toISOString()}>
                  {isCurrentRoundView ? (
                    <>
                      <strong>{picksLockDuration}</strong> left to save picks
                    </>
                  ) : (
                    <>
                      {selectedLeague.name}: <strong>{picksLockDuration}</strong> left to save picks
                    </>
                  )}
                </time>
                <AugmentHelpPopover
                  title="When picks lock"
                  triggerAriaLabel="When picks lock"
                  dialogAriaLabel="Picks lock explanation"
                >
                  <p className="augment-help-popover__lead">
                    Locks {picksLockExactLabel}. Picks lock {picksLockBufferMins} min before the first
                    kickoff.
                  </p>
                </AugmentHelpPopover>
              </>
            ) : (
              selectedLeague.name
            )}
          </p>
        )}

        <div className="league-selector">
          <LeagueCircleSelector
            leagues={preferredLeagues}
            selectedLeague={selectedLeague}
            onSelect={handleLeagueSelect}
          />
          <span className="league-name">{selectedLeague.name}</span>
        </div>
      </header>

      {showNoRounds && <div className="sc-error">No rounds available for {selectedLeague.name}</div>}

      {!loading && hasRounds && selectedRound && (
        <>
          {/* Round pills */}
          <div className="round-selector">
            {roundsData.previousRound && (
              <button
                type="button"
                className={`round-pill ${selectedRoundType === 'previous' ? 'round-pill--selected' : ''}`}
                onClick={() => selectRoundType('previous')}
              >
                ← Round {formatRoundDisplay(roundsData.previousRound.round)}
              </button>
            )}
            {roundsData.currentRound && (
              <button
                type="button"
                className={`round-pill ${selectedRoundType === 'current' ? 'round-pill--selected' : ''}`}
                onClick={() => selectRoundType('current')}
              >
                Round {formatRoundDisplay(roundsData.currentRound.round)}
              </button>
            )}
            {roundsData.nextRound && (
              <button
                type="button"
                className={`round-pill ${selectedRoundType === 'next' ? 'round-pill--selected' : ''}`}
                onClick={() => selectRoundType('next')}
              >
                Round {formatRoundDisplay(roundsData.nextRound.round)} →
              </button>
            )}
          </div>

          {(isRoundFinished || isRoundLocked || (selectedRound.totalPoints != null && selectedRound.pointBuckets)) && (
            <UserRoundPosition
              totalPoints={selectedRound.totalPoints}
              pointBuckets={selectedRound.pointBuckets}
              statusLabel={
                isRoundFinished ? 'Round finished' : isRoundLocked ? 'Round locked 🔒' : undefined
              }
            />
          )}

          {/* Sticky TP + match plays need this wrapper to extend through the games list (CSS sticky containing block) */}
          <div ref={stickyScrollHostRef} className="betting-sticky-scroll-host">
            {showAugmentControls && (
              <div ref={stickyMeterRef} className="augment-strip augment-sticky-meter">
                <div className="tactical-points-meter-bar">
                  <div
                    className="tactical-points-meter"
                    aria-label={`Tactical points used: ${tacticalPointsUsed} of ${tacticalPointsCap}`}
                  >
                    <div className="tactical-points-meter-row">
                      <span className="tactical-points-meter-label">Tactical points</span>
                      <span
                        className={`tactical-points-meter-count ${tacticalPointsUsed > tacticalPointsCap ? 'tactical-points-meter-count--over' : ''}`}
                      >
                        {tacticalPointsUsed}/{tacticalPointsCap}
                      </span>
                    </div>
                    <div className="tactical-points-meter-track">
                      <div
                        className={`tactical-points-meter-fill ${tacticalPointsUsed >= tacticalPointsCap ? 'tactical-points-meter-fill--full' : ''}`}
                        style={{ width: `${tacticalPointsPct}%` }}
                      />
                    </div>
                  </div>
                  <SeasonStackChip stack={seasonStack} className="picks-season-stack--meter" />
                  <button
                    type="submit"
                    form="betting-submit-form"
                    data-testid="picks-save-btn"
                    className={`sc-btn sc-btn-primary submit-bet-btn submit-bet-btn--sticky${hasUnsavedChanges ? ' submit-bet-btn--unsaved' : ''}${submitting ? ' submit-bet-btn--submitting' : ''}`}
                    disabled={submitting || isRoundFinished || !selectedRound.roundSubmissionOpen || !hasUnsavedChanges}
                    aria-busy={submitting || undefined}
                    aria-label={
                      submitting
                        ? 'Saving'
                        : hasUnsavedChanges
                          ? 'Save unsaved changes'
                          : 'No changes to save'
                    }
                  >
                    <span className="submit-bet-btn__content">
                      <span className={`submit-bet-btn__text${submitting ? ' submit-bet-btn__text--hidden' : ''}`}>
                        {hasUnsavedChanges ? 'Save •' : 'Save'}
                      </span>
                      {submitting ? <span className="submit-bet-btn__spinner" aria-hidden="true" /> : null}
                    </span>
                  </button>
                </div>
              </div>
            )}
            {showAugmentControls && (
              <div className="augment-strip augment-strip--round">
              <div className="augments-label-row augment-subsection-help-row">
                <h3 className="augments-label">Round play</h3>
                <AugmentHelpPopover
                  title="Round plays"
                  triggerAriaLabel="What do round plays do?"
                  dialogAriaLabel="Round play descriptions"
                >
                  <p className="augment-help-popover__lead">
                    {getRoundAugmentPoolHelpLead(tacticalPointsCap)}
                  </p>
                  <AugmentHelpItems
                    augments={getRoundAugmentsForHelp()}
                    prepend={
                      <div className="augment-help-popover__item">
                        <img src={ROUND_NONE_SVG} alt="" className="augment-help-popover__icon" />
                        <div>
                          <span className="augment-help-popover__name">None</span>
                          <span className="augment-help-popover__desc">No round-level bonus</span>
                        </div>
                      </div>
                    }
                  />
                </AugmentHelpPopover>
              </div>
              <div className="round-augment-section">
                <div className="round-augment-cards-shell">
                  <div className="augment-pool augment-pool--round-augment-only">
                    {showRoundAugmentCarousel && (
                      <button
                        type="button"
                        className="augment-carousel-btn augment-carousel-btn--prev"
                        aria-label="Previous round plays"
                        disabled={mobileAugmentScroll ? roundStripAtStart : roundAugmentCarouselOffset <= 0}
                        onClick={() => {
                          if (mobileAugmentScroll) {
                            scrollAugmentStripByCard(roundAugmentStripRef.current, -1);
                            return;
                          }
                          setRoundAugmentCarouselOffset((o) => Math.max(0, o - 1));
                        }}
                      />
                    )}
                    <div
                      ref={roundAugmentStripRef}
                      className={`augment-cards augment-cards--round-augment ${showRoundAugmentCarousel ? 'augment-cards--carousel' : ''}`}
                      role="radiogroup"
                      aria-label="Round play"
                      onScroll={
                        mobileAugmentScroll
                          ? () => {
                              const edges = augmentStripEdges(roundAugmentStripRef.current);
                              setRoundStripAtStart(edges.atStart);
                              setRoundStripAtEnd(edges.atEnd);
                            }
                          : undefined
                      }
                      style={
                        showRoundAugmentCarousel
                          ? {
                              '--augment-carousel-offset': roundAugmentCarouselOffset,
                              '--augment-carousel-visible': augmentsPerView
                            }
                          : undefined
                      }
                    >
                    <div
                      className={
                        showRoundAugmentCarousel
                          ? 'augment-cards-track'
                          : 'augment-cards-track augment-cards-track--static'
                      }
                    >
                    <label
                      className={`augment-card augment-card--neutral augment-card--round-augment ${!selectedRound.roundAugment ? 'augment-card--round-selected' : ''}`}
                      title="No round play"
                    >
                      <input
                        type="radio"
                        className="round-augment-sr-input"
                        name="roundAugment"
                        checked={!selectedRound.roundAugment}
                        onChange={() => handleRoundAugmentChange('')}
                      />
                      <span className="augment-card-tactical-cost" aria-hidden="true">
                        0
                      </span>
                      <span className="augment-card-desc">None</span>
                      <img src={ROUND_NONE_SVG} alt="" className="augment-card-img" />
                    </label>
                    {DEFAULT_ROUND_AUGMENTS.map((ra) => {
                      const inventory = user?.inventory ?? {};
                      const selected = getAugmentBaseCode(selectedRound.roundAugment ?? '') === ra.value;
                      const savedStyleId = selected && selectedRound.roundAugment
                        ? parseAugmentRef(selectedRound.roundAugment).styleId
                        : null;
                      const prefStyleId = getMatchPoolStyleId(ra.value);
                      const displayStyleId = selected ? (savedStyleId ?? prefStyleId) : prefStyleId;
                      const poolDisplaySvg =
                        getPoolAugmentDisplaySvg(ra.value, displayStyleId, inventory) ?? ra.svg;
                      const hasPoolStyles = getOwnedStyleIdsForPoolAugment(ra.value, inventory).length > 0;
                      const roundAugmentDisabled = !selectedRound.roundSubmissionOpen;
                      const roundTitle = ra.shortDesc || ra.desc;
                      return (
                        <label
                          key={ra.value}
                          className={`augment-card augment-card--${ra.color} augment-card--round-augment ${selected ? 'augment-card--round-selected' : ''}`}
                          title={roundTitle}
                        >
                          <input
                            type="radio"
                            className="round-augment-sr-input"
                            name="roundAugment"
                            checked={selected}
                            onChange={() => handleRoundAugmentChange(ra.value)}
                          />
                          <span className="augment-card-tactical-cost" aria-hidden="true">
                            {ra.tacticalPointsCost}
                          </span>
                          <span className="augment-card-desc">{getAugmentDisplayName(ra.value)}</span>
                          <img src={poolDisplaySvg} alt={getAugmentDisplayName(ra.value)} className="augment-card-img" />
                          {hasPoolStyles ? (
                            <AugmentPoolStyleDot
                              poolValue={ra.value}
                              inventory={inventory}
                              selectedStyleId={displayStyleId}
                              disabled={roundAugmentDisabled}
                              onSelect={(styleId) => handleRoundAugmentStyleChange(ra.value, styleId)}
                            />
                          ) : null}
                        </label>
                      );
                    })}
                    </div>
                    </div>
                    {showRoundAugmentCarousel && (
                      <button
                        type="button"
                        className="augment-carousel-btn augment-carousel-btn--next"
                        aria-label="Next round plays"
                        disabled={mobileAugmentScroll ? roundStripAtEnd : roundAugmentCarouselOffset >= roundAugmentCarouselMax}
                        onClick={() => {
                          if (mobileAugmentScroll) {
                            scrollAugmentStripByCard(roundAugmentStripRef.current, 1);
                            return;
                          }
                          setRoundAugmentCarouselOffset((o) => Math.min(roundAugmentCarouselMax, o + 1));
                        }}
                      />
                    )}
                  </div>
                  {roundAugmentSpreadWave != null && (
                    <div
                      key={roundAugmentSpreadWave.pulseId}
                      className={`round-augment-wave-flash round-augment-wave-flash--${roundAugmentSpreadWave.color}`}
                      aria-hidden
                    />
                  )}
                </div>
              </div>
              {slotPickerSelected && showAugmentControls && (
                <p className={`${slotPickerCss}-picker-hint`} role="status">
                  {slotPickerIconSvg && (
                    <img
                      src={slotPickerIconSvg}
                      alt=""
                      className="picker-hint-icon"
                    />
                  )}
                  <span>
                    {slotPickerLabel}: tap matches to include ({slotPickerTargets.length}/{slotPickerMax}).
                    {' '}{slotPickerHint}
                  </span>
                </p>
              )}
              </div>
            )}
            {showReadonlyRoundAugment && (
              <div className="augment-strip augment-strip--round augment-strip--readonly">
                <h3 className="augments-label">Round play</h3>
                <div
                  className={`round-augment-readonly${
                    isRoundFinished && getRoundAugmentApplied(selectedRound) === true
                      ? ' round-augment-readonly--applied'
                      : isRoundFinished && getRoundAugmentApplied(selectedRound) === false
                        ? ' round-augment-readonly--missed'
                        : ''
                  }`}
                >
                  <div
                    className={`augment-slot-btn augment-slot-btn--filled augment-slot-btn--${readonlyRoundAugmentColor} augment-slot-btn--static`}
                  >
                    <img
                      src={readonlyRoundAugmentSvg}
                      alt={getAugmentDisplayName(selectedRoundAugmentCode)}
                      className="augment-badge augment-badge-img"
                    />
                  </div>
                  <span className="round-augment-readonly-name">
                    {getAugmentDisplayName(selectedRoundAugmentCode)}
                  </span>
                  {isRoundFinished && selectedRound?.roundPoints != null && (
                    <span
                      className={`points-badge ${
                        selectedRound.roundPoints > 0
                          ? 'points-badge--win'
                          : selectedRound.roundPoints < 0
                            ? 'points-badge--loss'
                            : 'points-badge--zero'
                      }`}
                    >
                      {selectedRound.roundPoints} pts
                    </span>
                  )}
                </div>
              </div>
            )}
            {showAugmentControls && (
            <div className="augment-strip augment-sticky-games">
              <div className="augments-label-row augment-subsection-help-row">
                <h3 className="augments-label">Match plays</h3>
                <AugmentHelpPopover
                  title="Match plays"
                  triggerAriaLabel="What do match plays do?"
                  dialogAriaLabel="Match play descriptions"
                >
                  <p className="augment-help-popover__lead">{getMatchAugmentPoolHelpLead(tacticalPointsCap)}</p>
                  {isWcLeagueCode(selectedLeague?.code) && (
                    <p className="augment-help-popover__lead">
                      World Cup: scores settle on <strong>90 minutes</strong> unless you play Extra time or Shootout
                      plays on knockout matches. Rounds follow calendar dates, not FIFA stage names.
                    </p>
                  )}
                  <AugmentHelpItems augments={getMatchAugmentsForHelp()} />
                </AugmentHelpPopover>
                <div className="match-vote-label-group">
                  <img
                    src={MATCH_VOTE_ICON_SVG}
                    alt=""
                    className="match-vote-label-icon"
                  />
                  <span className="augments-label">Votes</span>
                  <span
                    className="match-vote-count"
                    aria-label={`${matchVotes.length} of ${MAX_MATCH_VOTES} votes used`}
                  >
                    {matchVotes.length}/{MAX_MATCH_VOTES}
                  </span>
                  <AugmentHelpPopover
                    title="Vote boost"
                    triggerAriaLabel="What does vote boost do?"
                    dialogAriaLabel="Vote boost explanation"
                  >
                    <p className="augment-help-popover__lead">
                      Tap the ballot icon on up to {MAX_MATCH_VOTES} match cards. The top 3 community
                      picks in this league and round get <strong>+0.1</strong> on that match&apos;s
                      points multiplier — but only for players who voted for that match (for example
                      1.2× becomes 1.3×).
                    </p>
                  </AugmentHelpPopover>
                </div>
              </div>
              <div className="augment-pool">
              {showAugmentCarousel && (
                <button
                  type="button"
                  className="augment-carousel-btn augment-carousel-btn--prev"
                  aria-label="Previous plays"
                  disabled={mobileAugmentScroll ? matchStripAtStart : augmentCarouselOffset <= 0}
                  onClick={() => {
                    if (mobileAugmentScroll) {
                      scrollAugmentStripByCard(matchAugmentStripRef.current, -1);
                      return;
                    }
                    setAugmentCarouselOffset((o) => Math.max(0, o - 1));
                  }}
                />
              )}
              <div
                ref={matchAugmentStripRef}
                className={`augment-cards ${showAugmentCarousel ? 'augment-cards--carousel' : ''} ${draggingFromGameId ? 'augment-cards--glow' : ''}`}
                onScroll={
                  mobileAugmentScroll
                    ? () => {
                        const edges = augmentStripEdges(matchAugmentStripRef.current);
                        setMatchStripAtStart(edges.atStart);
                        setMatchStripAtEnd(edges.atEnd);
                      }
                    : undefined
                }
                style={
                  showAugmentCarousel
                    ? {
                        '--augment-carousel-offset': augmentCarouselOffset,
                        '--augment-carousel-visible': augmentsPerView,
                      }
                    : undefined
                }
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = draggingFromGameId ? 'move' : 'none';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  try {
                    const data = JSON.parse(e.dataTransfer.getData('application/json'));
                    if (data.source === 'game' && data.sourceGameId) {
                      handleAugmentRemove(data.sourceGameId);
                    }
                  } catch (_) {}
                  setDraggingAugment(null);
                  setDraggingFromGameId(null);
                }}
              >
              <div className={showAugmentCarousel ? 'augment-cards-track' : 'augment-cards-track augment-cards-track--static'}>
              {playerAugments.map((a, index) => {
                const inventory = user?.inventory ?? {};
                const poolStyleId = getMatchPoolStyleId(a.value);
                const poolDisplaySvg =
                  getPoolAugmentDisplaySvg(a.value, poolStyleId, inventory) ?? a.svg;
                const hasPoolStyles = getOwnedStyleIdsForPoolAugment(a.value, inventory).length > 0;
                const teamSidePool = getTeamSideUiPoolState(a.value, placedAugmentBases);
                const isTeamSideUi = teamSidePool.group != null;
                const isParlayUi = a.value === PARLAY_AUGMENT_CODE;
                const usedOn = isTeamSideUi || isParlayUi ? null : augmentUsedByGame[a.value];
                const isUsed = isTeamSideUi
                  ? teamSidePool.isUsed
                  : isParlayUi
                    ? parlayPlacementCount >= PARLAY_MAX_USES
                    : !!usedOn;
                const isPartial = (isTeamSideUi && teamSidePool.isPartial) ||
                  (isParlayUi && parlayPlacementCount === 1 && !parlayPairComplete);
                const isDragging = draggingAugment === a.value;
                const sacrificeFollowUpLocked =
                  (a.value === 'm' || a.value === BOUNTY_AUGMENT_CODE) && !augmentUsedByGame.d;
                const sacrificeOtherFollowUpUsed =
                  (a.value === 'm' && !!augmentUsedByGame[BOUNTY_AUGMENT_CODE]) ||
                  (a.value === BOUNTY_AUGMENT_CODE && !!augmentUsedByGame.m);
                const bountyStackLocked =
                  a.value === BOUNTY_AUGMENT_CODE &&
                  availableBountyStakeForRound(selectedRound, roundsData?.seasonStack) <
                    BOUNTY_STAKE_MIN;
                const augmentDisabled =
                  isUsed ||
                  sacrificeFollowUpLocked ||
                  sacrificeOtherFollowUpUsed ||
                  bountyStackLocked;
                const poolTitle = sacrificeFollowUpLocked
                  ? `Place Sacrifice on a match first, then drag ${a.fullLabel} onto another match.`
                  : sacrificeOtherFollowUpUsed
                  ? a.value === 'm'
                    ? 'Bounty is already on a match. Remove it to use Multiplier instead.'
                    : 'Multiplier is already on a match. Remove it to use Bounty instead.'
                  : bountyStackLocked
                  ? `Bounty needs at least ${BOUNTY_STAKE_MIN} Stack.`
                  : isParlayUi
                  ? isUsed
                    ? 'Parlay linked on two matches. Drag from a match to remove.'
                    : isPartial
                      ? 'Parlay on one match — drop onto a second match to link (+6 each if both 1×2 correct).'
                      : `${a.shortDesc || a.desc} Drag onto two matches to link.`
                  : isTeamSideUi
                  ? isUsed
                    ? `${teamSidePool.group.uiAugment.displayName} used twice this round. Drag from a match to remove.`
                    : isPartial
                      ? `${teamSidePool.useCount} of 2 placed — drop again on home or away.`
                      : `${a.shortDesc || a.desc} Drag to home or away.`
                  : isUsed
                    ? `${a.shortDesc || a.desc}. Drag to plays area to remove.`
                    : `${a.shortDesc || a.desc}. Drag to a match to add.`;
                const uiCardClass =
                  a.value === 'g'
                    ? 'augment-card--plus1-goal'
                    : a.value === PENALTY_UI_VALUE
                      ? 'augment-card--penalty'
                      : a.value === MISSED_PENALTY_UI_VALUE
                        ? 'augment-card--missed-penalty'
                      : a.value === 'm'
                          ? 'augment-card--sacrifice-mult'
                          : a.value === BOUNTY_AUGMENT_CODE
                            ? 'augment-card--bounty'
                          : a.value === HOURGLASS_AUGMENT_CODE
                            ? 'augment-card--hourglass'
                          : '';
                const offscreen =
                  showAugmentCarousel &&
                  !mobileAugmentScroll &&
                  (index < augmentCarouselOffset || index >= augmentCarouselOffset + augmentsPerView);
                return (
                  <div
                    key={a.value}
                    draggable={!augmentDisabled}
                    ref={(node) => claimPoolTouchDrag(node, () => touchDragDataRef.current != null)}
                    className={`augment-card augment-card--${a.color} ${isUsed ? 'augment-card--used' : ''} ${isPartial ? 'augment-card--partial' : ''} ${isDragging ? 'augment-card--dragging' : ''} ${offscreen ? 'augment-card--offscreen' : ''} ${sacrificeFollowUpLocked || sacrificeOtherFollowUpUsed || bountyStackLocked ? 'augment-card--disabled' : ''} ${uiCardClass}`}
                    onDragStart={(e) => {
                      if (shouldIgnoreNativeDragDuringTouch(touchDragPendingRef.current, touchDragDataRef.current)) {
                        e.preventDefault();
                        return;
                      }
                      if (augmentDisabled) return;
                      setDraggingAugment(a.value);
                      setDraggingFromGameId(null);
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify(buildPoolDragPayload(a.value, 'pool'))
                      );
                      e.dataTransfer.effectAllowed = 'copyMove';
                      setScaledDragImage(e, e.currentTarget);
                    }}
                    onDragEnd={() => {
                      if (shouldIgnoreNativeDragDuringTouch(touchDragPendingRef.current, touchDragDataRef.current)) return;
                      setDraggingAugment(null);
                      setDraggingFromGameId(null);
                      setDragOverSide(null);
                    }}
                    onTouchStart={(e) => {
                      if (augmentDisabled) return;
                      const touch = e.touches[0];
                      const startPos = { x: touch.clientX, y: touch.clientY };
                      const payload = buildPoolDragPayload(a.value, 'pool');
                      clearMatchAugmentTouchDragHold();
                      touchDragPendingRef.current = {
                        startPos,
                        payload,
                        sourceEl: e.currentTarget,
                        draggingAugment: a.value,
                        draggingFromGameId: null
                      };
                      setTouchStartPos(startPos);
                      scheduleTouchDragHold(touchDragHoldTimerRef, armMatchAugmentTouchDrag);
                    }}
                    onTouchMove={(e) => {
                      const touch = e.touches[0];
                      const pos = { x: touch.clientX, y: touch.clientY };
                      if (poolSwipeRef.current) {
                        poolSwipeRef.current = applyPoolCarouselSwipe(poolSwipeRef.current, pos.x);
                        return;
                      }
                      if (touchDragPendingRef.current) {
                        if (shouldCancelTouchDragHold(touchDragPendingRef.current.startPos, pos)) {
                          clearMatchAugmentTouchDragHold();
                          setTouchStartPos(null);
                          const scroller = e.currentTarget.closest('.augment-cards--carousel');
                          if (scroller) poolSwipeRef.current = { scroller, lastX: pos.x };
                        }
                        return;
                      }
                      const dragData = touchDragDataRef.current;
                      if (!dragData || isUsed) return;
                      e.preventDefault();
                      touchDragLastPointRef.current = pos;
                      setTouchCurrentPos(pos);
                      const element = elementUnderTouch(touch.clientX, touch.clientY);
                      const gameCard = element?.closest('.game-card[data-game-id]');
                      const augmentArea =
                        element?.closest('.augment-sticky-games') || element?.closest('.augment-pool');
                      if (gameCard) {
                        setDragOverGameId(gameCard.dataset.gameId);
                        if (isTeamSideAugmentUiDrag(dragData.value)) {
                          setDragOverSide(getDropSideFromPointerEvent({ clientX: touch.clientX, target: element }, gameCard));
                        }
                      } else if (augmentArea && dragData.source === 'game') {
                        setDragOverGameId('augment-area');
                        setDragOverSide(null);
                      } else {
                        setDragOverGameId(null);
                        setDragOverSide(null);
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (poolSwipeRef.current) {
                        poolSwipeRef.current = null;
                        return;
                      }
                      if (touchDragPendingRef.current) {
                        clearMatchAugmentTouchDragHold();
                        setTouchStartPos(null);
                        return;
                      }
                      const touch = e.changedTouches[0];
                      const eventPoint = touch ? { x: touch.clientX, y: touch.clientY } : null;
                      applyMatchTouchDropRef.current(
                        chooseMatchTouchDropPoint(eventPoint, touchDragLastPointRef.current)
                      );
                    }}
                    title={poolTitle}
                  >
                    <span className="augment-card-tactical-cost" aria-label={`${a.tacticalPointsCost} tactical points`}>
                      {a.tacticalPointsCost}
                    </span>
                    <span className="augment-card-desc">{a.displayName}</span>
                    <img src={poolDisplaySvg} alt={a.displayName} className="augment-card-img" />
                    {hasPoolStyles ? (
                      <AugmentPoolStyleDot
                        poolValue={a.value}
                        inventory={inventory}
                        selectedStyleId={poolStyleId}
                        disabled={!selectedRound.roundSubmissionOpen}
                        onSelect={(styleId) => selectMatchPoolStyle(a.value, styleId)}
                      />
                    ) : null}
                  </div>
                );
              })}
              </div>
              </div>
              {showAugmentCarousel && (
                <button
                  type="button"
                  className="augment-carousel-btn augment-carousel-btn--next"
                  aria-label="Next plays"
                  disabled={mobileAugmentScroll ? matchStripAtEnd : augmentCarouselOffset >= augmentCarouselMax}
                  onClick={() => {
                    if (mobileAugmentScroll) {
                      scrollAugmentStripByCard(matchAugmentStripRef.current, 1);
                      return;
                    }
                    setAugmentCarouselOffset((o) => Math.min(augmentCarouselMax, o + 1));
                  }}
                />
              )}
            </div>
            </div>
            )}

          {error && (
            <div className="sc-error betting-error-floating" role="alert">
              <span className="betting-error-floating__text">{error}</span>
              <button
                type="button"
                className="betting-error-floating__dismiss"
                aria-label="Dismiss message"
                onClick={() => setError('')}
              >
                ×
              </button>
            </div>
          )}
          {success && (
            <div className="betting-success-floating" role="status" aria-live="polite">
              <span className="betting-success-floating__text">{success}</span>
              <button
                type="button"
                className="betting-success-floating__dismiss"
                aria-label="Dismiss message"
                onClick={() => setSuccess('')}
              >
                ×
              </button>
            </div>
          )}

          <form id="betting-submit-form" onSubmit={handleSubmit}>
            <div className="games-list">
              {displayGames.map(game => {
                const gameFinished = !isGameOnRoundCard(game);
                const gameVoided = isVoidedGame(game);
                const gameLive = game.status === 'live' || game.status === 'in_play' || game.status === 'active';
                const bet = selectedRound.bets?.[String(game.gameId)] ?? selectedRound.bets?.[game.gameId];
                const hasPlacedPick = hasPlacedBet({ bets: selectedRound.serverBets ?? {} }, game.gameId);
                const showScoredPickUi = (gameFinished || gameLive) && !gameVoided;

                const gameLocked = !selectedRound.roundSubmissionOpen && !gameFinished;
                const canEditPicks =
                  selectedRound.roundSubmissionOpen && isGameOnRoundCard(game) && !gameLive;
                const isHourglassTarget = isHourglassTargetGame(game);
                const hourglassDropOpen =
                  draggingAugment != null &&
                  getAugmentBaseCode(draggingAugment) === HOURGLASS_AUGMENT_CODE &&
                  isHourglassTarget;
                const isDroppable = canEditPicks || isHourglassTarget;
                const matchAugment = bet?.augment;
                const canDragHourglassOff =
                  getAugmentBaseCode(matchAugment) === HOURGLASS_AUGMENT_CODE &&
                  isHourglassPlacementOpenForGame(game) &&
                  canMoveHourglass(bet?.hourglassPoints);
                const canDragAugmentOff =
                  (selectedRound.roundSubmissionOpen &&
                    !gameLive &&
                    (canEditPicks || canRemoveAugmentFromGame(game))) ||
                  canDragHourglassOff;
                const centerAugment =
                  matchAugment && !isTeamSideAugmentVariant(matchAugment) ? matchAugment : null;
                const homeTeamAugment =
                  matchAugment && isHomeTeamSideAugment(matchAugment) ? matchAugment : null;
                const awayTeamAugment =
                  matchAugment && isAwayTeamSideAugment(matchAugment) ? matchAugment : null;
                const emptyDropLabel = draggingTeamSideGroup?.uiAugment?.displayName ?? 'One Goal';
                const isDragOverThis =
                  dragOverGameId != null && sameGameId(dragOverGameId, game.gameId);
                const teamDragOverClass = (side) => {
                  if (!isDragOverThis || dragOverSide !== side || !draggingTeamSideGroup) return '';
                  return `game-team--augment-drag-over game-team--augment-drag-over--${draggingTeamSideGroup.uiAugment.color}`;
                };
                const matchAugmentApplied = getMatchAugmentApplied(bet);
                const renderAugmentBadge = (augValue, { draggable: canDrag, onDragProps, onTouchProps } = {}) => {
                  if (!augValue) {
                    return <span className="augment-empty">{emptyDropLabel}</span>;
                  }
                  const meta = getAugmentByValue(augValue);
                  const color = meta?.color || 'gold';
                  const displaySvg = getAugmentDisplaySvg(augValue, user?.inventory) ?? meta?.svg;
                  const appliedClass =
                    gameFinished && matchAugmentApplied === true
                      ? ' augment-slot-btn--applied'
                      : gameFinished && matchAugmentApplied === false
                        ? ' augment-slot-btn--missed'
                        : '';
                  const slotClass = `augment-slot-btn augment-slot-btn--filled augment-slot-btn--${color}${appliedClass}`;
                  const hourglassPts =
                    getAugmentBaseCode(augValue) === HOURGLASS_AUGMENT_CODE
                      ? resolveHourglassPoints(bet?.hourglassPoints)
                      : null;
                  const hourglassOverlay =
                    hourglassPts != null ? (
                      <span className="hourglass-pts">+{hourglassPts}</span>
                    ) : null;
                  if (canDrag && canDragAugmentOff) {
                    return (
                      <div
                        draggable
                        ref={claimTouchDragHandle}
                        className={`${slotClass} augment-slot-btn--draggable`}
                        title={getAugmentDisplayName(augValue)}
                        {...onDragProps}
                        {...onTouchProps}
                      >
                        <img src={displaySvg} alt={getAugmentDisplayName(augValue)} className="augment-badge augment-badge-img" />
                        {hourglassOverlay}
                      </div>
                    );
                  }
                  return (
                    <div className={`${slotClass} augment-slot-btn--static`}>
                      <img src={displaySvg} alt={getAugmentDisplayName(augValue)} className="augment-badge augment-badge-img" />
                      {hourglassOverlay}
                    </div>
                  );
                };
                const teamSideDragHandlers = (augValue) => ({
                  onDragStart: (e) => {
                    if (shouldIgnoreNativeDragDuringTouch(touchDragPendingRef.current, touchDragDataRef.current)) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    setDraggingAugment(augValue);
                    setDraggingFromGameId(game.gameId);
                    e.dataTransfer.setData(
                      'application/json',
                      JSON.stringify({ value: augValue, source: 'game', sourceGameId: game.gameId })
                    );
                    e.dataTransfer.effectAllowed = 'move';
                    setScaledDragImage(e, e.currentTarget);
                  },
                  onDragEnd: () => {
                    if (shouldIgnoreNativeDragDuringTouch(touchDragPendingRef.current, touchDragDataRef.current)) return;
                    setDraggingAugment(null);
                    setDraggingFromGameId(null);
                    setDragOverSide(null);
                  },
                  onTouchStart: (e) => {
                    e.stopPropagation();
                    const touch = e.touches[0];
                    const startPos = { x: touch.clientX, y: touch.clientY };
                    const payload = { value: augValue, source: 'game', sourceGameId: game.gameId };
                    clearMatchAugmentTouchDragHold();
                    touchDragPendingRef.current = {
                      startPos,
                      payload,
                      sourceEl: e.currentTarget,
                      draggingAugment: augValue,
                      draggingFromGameId: game.gameId
                    };
                    setTouchStartPos(startPos);
                    scheduleTouchDragHold(touchDragHoldTimerRef, armMatchAugmentTouchDrag);
                  },
                  onTouchMove: (e) => {
                    const touch = e.touches[0];
                    const pos = { x: touch.clientX, y: touch.clientY };
                    if (touchDragPendingRef.current) {
                      if (shouldCancelTouchDragHold(touchDragPendingRef.current.startPos, pos)) {
                        clearMatchAugmentTouchDragHold();
                        setTouchStartPos(null);
                      }
                      return;
                    }
                    const dragData = touchDragDataRef.current;
                    if (!dragData) return;
                    e.preventDefault();
                    e.stopPropagation();
                    touchDragLastPointRef.current = pos;
                    setTouchCurrentPos(pos);
                    const element = elementUnderTouch(touch.clientX, touch.clientY);
                    const gameCardEl = isPointOverMatchPlays(pos)
                      ? null
                      : element?.closest('.game-card[data-game-id]');
                    const augmentArea =
                      isPointOverMatchPlays(pos) ||
                      element?.closest('.augment-sticky-games') ||
                      element?.closest('.augment-pool');
                    if (gameCardEl) {
                      setDragOverGameId(gameCardEl.dataset.gameId);
                      if (isTeamSideAugmentUiDrag(dragData.value)) {
                        setDragOverSide(getDropSideFromPointerEvent({ clientX: touch.clientX, target: element }, gameCardEl));
                      }
                    } else if (augmentArea) {
                      setDragOverGameId('augment-area');
                      setDragOverSide(null);
                    } else {
                      setDragOverGameId(null);
                      setDragOverSide(null);
                    }
                  },
                  onTouchEnd: (e) => {
                    if (touchDragPendingRef.current) {
                      clearMatchAugmentTouchDragHold();
                      setTouchStartPos(null);
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    const touch = e.changedTouches[0];
                    const eventPoint = touch ? { x: touch.clientX, y: touch.clientY } : null;
                    applyMatchTouchDropRef.current(
                      chooseMatchTouchDropPoint(eventPoint, touchDragLastPointRef.current)
                    );
                  }
                });
                const centerDragHandlers = centerAugment
                  ? teamSideDragHandlers(centerAugment)
                  : null;
                const splitParsed =
                  matchAugment && getAugmentBaseCode(matchAugment) === 's'
                    ? parseAugmentRef(matchAugment)
                    : null;
                const splitPayload = splitParsed?.split ?? null;
                const splitPrimaryWeight = splitPayload
                  ? SPLIT_TOTAL_WEIGHT - splitPayload.weight
                  : null;
                const bountyOnMatch =
                  matchAugment && getAugmentBaseCode(matchAugment) === BOUNTY_AUGMENT_CODE;
                const bountyStakeCap = Math.min(
                  BOUNTY_STAKE_MAX,
                  availableBountyStakeForRound(selectedRound, roundsData?.seasonStack)
                );
                const bountyStake = bountyOnMatch
                  ? clampBountyStake(bet?.bountyPoints, bountyStakeCap) || BOUNTY_STAKE_MIN
                  : null;
                const augmentSpreadActive = matchAugmentSpreadWave?.targets.has(String(game.gameId));
                const augmentSpreadSvg = augmentSpreadActive ? matchAugmentSpreadWave.svg : null;
                const augmentSpreadStyle = augmentSpreadSvg
                  ? { '--augment-spread-svg': `url("${augmentSpreadSvg}")` }
                  : undefined;
                const showMatchAugmentSlots =
                  showAugmentControls ||
                  !!(homeTeamAugment || centerAugment || awayTeamAugment);
                const gameSlot = gameSlotFromId(game.gameId);
                const isSacrificedMatch =
                  !!sacrificeDeadEntry && sameGameId(sacrificeDeadEntry.gameId, game.gameId);
                const slotPickerOn = gameSlot != null && slotPickerTargetSet.has(gameSlot);
                const slotPickerToggleDisabled =
                  !showAugmentControls ||
                  !slotPickerSelected ||
                  isSacrificedMatch ||
                  (!slotPickerOn && slotPickerTargets.length >= slotPickerMax);
                const voteOn = matchVotes.some((v) => sameGameId(v, game.gameId));
                const voteBoosted = game.pickStats?.voteBoosted === true;
                const voteToggleDisabled =
                  !showAugmentControls ||
                  isSacrificedMatch ||
                  (!voteOn && matchVotes.length >= MAX_MATCH_VOTES);
                return (
                  <div
                    key={game.gameId}
                    data-game-id={game.gameId}
                    style={augmentSpreadStyle}
                    className={`game-card ${gameLocked && !gameFinished ? 'game-card--locked' : ''} ${gameLive ? 'game-card--live' : ''} ${gameFinished ? 'game-card--finished' : ''} ${canEditPicks || hourglassDropOpen ? 'game-card--droppable' : ''} ${isDragOverThis ? 'game-card--drag-over' : ''} ${isDragOverThis && isDraggingTeamSideAugment ? 'game-card--team-side-drag' : ''} ${augmentSpreadActive ? `game-card--augment-spread game-card--augment-${matchAugmentSpreadWave.color}${augmentSpreadSvg ? ' game-card--augment-spread-svg' : ''}` : ''} ${slotPickerOn ? `game-card--${slotPickerCss}` : ''}`}
                    onDragOver={
                      isDroppable
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverGameId(String(game.gameId));
                            if (isDraggingTeamSideAugment) {
                              setDragOverSide(getDropSideFromPointerEvent(e, e.currentTarget));
                            } else {
                              setDragOverSide(null);
                            }
                          }
                        : undefined
                    }
                    onDragLeave={
                      isDroppable
                        ? (e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setDragOverGameId(null);
                              setDragOverSide(null);
                            }
                          }
                        : undefined
                    }
                    onDrop={
                      isDroppable
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragOverGameId(null);
                            setDragOverSide(null);
                            try {
                              const data = JSON.parse(e.dataTransfer.getData('application/json'));
                              handleAugmentDrop(
                                game.gameId,
                                data,
                                getDropSideFromPointerEvent(e, e.currentTarget),
                                e.currentTarget
                              );
                            } catch (_) {}
                            setDraggingAugment(null);
                            setDraggingFromGameId(null);
                          }
                        : undefined
                    }
                    onTouchMove={
                      isDroppable && touchDragData
                        ? (e) => {
                            const touch = e.touches[0];
                            setTouchCurrentPos({ x: touch.clientX, y: touch.clientY });
                            const element = elementUnderTouch(touch.clientX, touch.clientY);
                            const gameCard = element?.closest('.game-card');
                            if (sameGameId(gameCard?.dataset.gameId, game.gameId)) {
                              setDragOverGameId(String(game.gameId));
                              if (isTeamSideAugmentUiDrag(touchDragData.value)) {
                                setDragOverSide(
                                  getDropSideFromPointerEvent({ clientX: touch.clientX, target: element }, gameCard)
                                );
                              }
                            } else {
                              setDragOverGameId(null);
                              setDragOverSide(null);
                            }
                          }
                        : undefined
                    }
                    onTouchEnd={
                      isDroppable && touchDragData
                        ? (e) => {
                            if (!touchDragDataRef.current) return;
                            const touch = e.changedTouches[0];
                            const eventPoint = touch ? { x: touch.clientX, y: touch.clientY } : null;
                            const point = chooseMatchTouchDropPoint(eventPoint, touchDragLastPointRef.current);
                            if (isPointOverMatchPlays(point)) {
                              applyMatchTouchDropRef.current(point);
                              return;
                            }
                            const element = elementUnderTouch(touch.clientX, touch.clientY);
                            const gameCard = element?.closest('.game-card');
                            if (sameGameId(gameCard?.dataset.gameId, game.gameId)) {
                              touchDragDataRef.current = null;
                              handleAugmentDrop(
                                game.gameId,
                                touchDragData,
                                getDropSideFromPointerEvent({ clientX: touch.clientX, target: element }, gameCard),
                                gameCard
                              );
                            }
                            resetMatchAugmentTouchDragState();
                          }
                        : undefined
                    }
                  >
                    <div className="game-card-header">
                      <span className="game-date">{new Date(game.gameDate).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <div className="game-card-header-right">
                        {showAugmentControls && (
                          <button
                            type="button"
                            className={`match-vote-toggle ${voteOn ? 'match-vote-toggle--on' : ''}`}
                            aria-pressed={voteOn}
                            aria-label={
                              voteOn
                                ? 'Remove vote boost from this match'
                                : 'Vote for +0.1 multiplier on this match'
                            }
                            disabled={!canEditPicks || (voteToggleDisabled && !voteOn)}
                            onClick={() => handleMatchVoteToggle(game.gameId)}
                          >
                            <img
                              src={MATCH_VOTE_ICON_SVG}
                              alt=""
                              className="match-vote-toggle-icon"
                            />
                          </button>
                        )}
                        {!showAugmentControls && voteOn && (
                          <span
                            className={`match-vote-badge ${voteBoosted ? 'match-vote-badge--in' : 'match-vote-badge--out'}`}
                            title={
                              voteBoosted
                                ? 'You voted for this match and it finished in the community Top 3 — +0.1 on your points multiplier'
                                : 'You voted for this match, but it did not make the community Top 3 — no +0.1 boost'
                            }
                          >
                            <img
                              src={MATCH_VOTE_ICON_SVG}
                              alt=""
                              className="match-vote-toggle-icon"
                            />
                            <span>{voteBoosted ? 'Voted · Top 3' : 'Voted'}</span>
                          </span>
                        )}
                        {!showAugmentControls && !voteOn && voteBoosted && (
                          <span
                            className="match-vote-badge match-vote-badge--community"
                            title="Community Top 3 — players who voted for this match get +0.1 on their points multiplier. You did not vote for this one."
                          >
                            <span>Top 3</span>
                          </span>
                        )}
                        {slotPickerSelected && showAugmentControls && slotPickerIconSvg && (
                          <button
                            type="button"
                            className={`${slotPickerCss}-toggle ${slotPickerOn ? `${slotPickerCss}-toggle--on` : ''}`}
                            aria-pressed={slotPickerOn}
                            aria-label={
                              slotPickerOn
                                ? `Remove ${slotPickerLabel} from this match`
                                : `Add ${slotPickerLabel} to this match`
                            }
                            disabled={!canEditPicks || (slotPickerToggleDisabled && !slotPickerOn)}
                            onClick={() => handleRoundAugmentSlotToggle(game.gameId)}
                          >
                            <img
                              src={slotPickerIconSvg}
                              alt=""
                              className={`${slotPickerCss}-toggle-icon`}
                            />
                          </button>
                        )}
                        {slotPickerOn && !showAugmentControls && slotPickerIconSvg && (
                          <span className={`${slotPickerCss}-badge`} title={slotPickerLabel}>
                            <img
                              src={slotPickerIconSvg}
                              alt={slotPickerLabel}
                              className={`${slotPickerCss}-toggle-icon`}
                            />
                          </span>
                        )}
                        {gameFinished && (
                          <span className="game-status game-status--finished">
                            {getGameStatusLabel(game, selectedRound?.games || displayGames)}
                          </span>
                        )}
                        {gameLive && <LiveMatchBadge game={game} />}
                        {gameLocked && !gameFinished && !gameLive && (
                          <span className="game-status game-status--upcoming">Scheduled</span>
                        )}
                        <MatchLeagueTablePopover
                          leagueCode={selectedLeague.code}
                          homeTeam={game.homeTeam}
                          awayTeam={game.awayTeam}
                          stage={game.stage}
                          games={selectedRound?.games || displayGames}
                        />
                      </div>
                    </div>

                    <div className="game-teams">
                      <div
                        className={`game-team game-team--home ${teamDragOverClass('home')} ${homeTeamAugment ? 'game-team--has-augment' : ''}`}
                        data-augment-drop-side="home"
                      >
                        <div className="team-head">
                          <ClubCrest leagueCode={selectedLeague.code} teamLabel={game.homeTeam} teamTla={game.homeTeamTla} />
                          <TeamName name={game.homeTeam} />
                        </div>
                        {gameFinished || gameLive ? (
                          <span className="team-score">{game.homeScore ?? '-'}</span>
                        ) : (
                          <div className="score-control">
                            <button type="button" className="score-arrow score-arrow-left" onClick={() => handleBetChange(game.gameId, clampScore((bet?.predictedHomeScore ?? 0) - 1), bet?.predictedAwayScore ?? 0)} disabled={!canEditPicks || (bet?.predictedHomeScore ?? 0) === 0} aria-label="Decrease home score">◀</button>
                            <span className="score-input score-input--display" tabIndex={-1}>{bet?.predictedHomeScore ?? 0}</span>
                            <button type="button" className="score-arrow score-arrow-right" onClick={() => handleBetChange(game.gameId, clampScore((bet?.predictedHomeScore ?? 0) + 1), bet?.predictedAwayScore ?? 0)} disabled={!canEditPicks} aria-label="Increase home score">▶</button>
                          </div>
                        )}
                        {showMatchAugmentSlots && homeTeamAugment && (
                          <div
                            className={`game-team-augment-slot game-team-augment-slot--home ${isDroppable && isDraggingTeamSideAugment ? 'game-team-augment-slot--droppable' : ''}`}
                            aria-label="Home team play"
                          >
                            {renderAugmentBadge(homeTeamAugment, {
                              draggable: canDragAugmentOff,
                              onDragProps: teamSideDragHandlers(homeTeamAugment),
                              onTouchProps: teamSideDragHandlers(homeTeamAugment)
                            })}
                          </div>
                        )}
                        {showAugmentControls && !homeTeamAugment && (
                          <div
                            className={`game-team-augment-slot game-team-augment-slot--home ${isDroppable && isDraggingTeamSideAugment ? 'game-team-augment-slot--droppable' : ''}`}
                            aria-label="Home team play drop zone"
                          >
                            {isDroppable && isDraggingTeamSideAugment ? renderAugmentBadge(null) : null}
                          </div>
                        )}
                      </div>
                      <div
                        className={`game-vs-augment${showMatchAugmentSlots ? '' : ' game-vs-augment--compact'}`}
                      >
                        <span className="game-vs">vs</span>
                        {showScoredPickUi && (
                          isSacrificedMatch ? (
                            <span className="game-your-bet game-your-bet--no-pick" title="Sacrificed">
                              Sacrificed
                            </span>
                          ) : hasPlacedPick ? (
                            <span className="game-your-bet" title="Your prediction">
                              {(bet.predictedHomeScore ?? '-')}–{(bet.predictedAwayScore ?? '-')}
                              {splitPayload ? (
                                <span className="game-your-bet-split">
                                  {' '}· {splitPayload.home}–{splitPayload.away}
                                </span>
                              ) : null}
                            </span>
                          ) : (
                            <span className="game-your-bet game-your-bet--no-pick" title="No pick placed">
                              No pick
                            </span>
                          )
                        )}
                        {showMatchAugmentSlots && centerAugment && (
                          <div className="game-augment-slot game-augment-slot--center">
                            {renderAugmentBadge(centerAugment, {
                              draggable: canDragAugmentOff,
                              onDragProps: centerDragHandlers,
                              onTouchProps: centerDragHandlers
                            })}
                          </div>
                        )}
                        {splitPrimaryWeight != null && !(gameFinished || gameLive) && (
                          <span className="split-weight-chip split-weight-chip--primary" title="Points on primary scoreline">
                            {splitPrimaryWeight} pts
                          </span>
                        )}
                        {showAugmentControls && !centerAugment && (
                          <div className="game-augment-slot game-augment-slot--center">
                            {isDroppable && !isDraggingTeamSideAugment ? (
                              <span className="augment-empty">—</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                      <div
                        className={`game-team game-team--away ${teamDragOverClass('away')} ${awayTeamAugment ? 'game-team--has-augment' : ''}`}
                        data-augment-drop-side="away"
                      >
                        <div className="team-head">
                          <ClubCrest leagueCode={selectedLeague.code} teamLabel={game.awayTeam} teamTla={game.awayTeamTla} />
                          <TeamName name={game.awayTeam} />
                        </div>
                        {gameFinished || gameLive ? (
                          <span className="team-score">{game.awayScore ?? '-'}</span>
                        ) : (
                          <div className="score-control">
                            <button type="button" className="score-arrow score-arrow-left" onClick={() => handleBetChange(game.gameId, bet?.predictedHomeScore ?? 0, clampScore((bet?.predictedAwayScore ?? 0) - 1))} disabled={!canEditPicks || (bet?.predictedAwayScore ?? 0) === 0} aria-label="Decrease away score">◀</button>
                            <span className="score-input score-input--display" tabIndex={-1}>{bet?.predictedAwayScore ?? 0}</span>
                            <button type="button" className="score-arrow score-arrow-right" onClick={() => handleBetChange(game.gameId, bet?.predictedHomeScore ?? 0, clampScore((bet?.predictedAwayScore ?? 0) + 1))} disabled={!canEditPicks} aria-label="Increase away score">▶</button>
                          </div>
                        )}
                        {showMatchAugmentSlots && awayTeamAugment && (
                          <div
                            className={`game-team-augment-slot game-team-augment-slot--away ${isDroppable && isDraggingTeamSideAugment ? 'game-team-augment-slot--droppable' : ''}`}
                            aria-label="Away team play"
                          >
                            {renderAugmentBadge(awayTeamAugment, {
                              draggable: canDragAugmentOff,
                              onDragProps: teamSideDragHandlers(awayTeamAugment),
                              onTouchProps: teamSideDragHandlers(awayTeamAugment)
                            })}
                          </div>
                        )}
                        {showAugmentControls && !awayTeamAugment && (
                          <div
                            className={`game-team-augment-slot game-team-augment-slot--away ${isDroppable && isDraggingTeamSideAugment ? 'game-team-augment-slot--droppable' : ''}`}
                            aria-label="Away team play drop zone"
                          >
                            {isDroppable && isDraggingTeamSideAugment ? renderAugmentBadge(null) : null}
                          </div>
                        )}
                      </div>
                    </div>

                    {splitPayload && (
                      <div className={`split-editor${canEditPicks ? '' : ' split-editor--readonly'}`}>
                        <div className="split-editor__scores">
                          {canEditPicks ? (
                            <>
                              <div className="score-control score-control--compact">
                                <button
                                  type="button"
                                  className="score-arrow score-arrow-left"
                                  onClick={() =>
                                    handleSplitSecondChange(
                                      game.gameId,
                                      clampScore(splitPayload.home - 1),
                                      splitPayload.away,
                                      splitPayload.weight
                                    )
                                  }
                                  disabled={splitPayload.home === 0}
                                  aria-label="Decrease split home score"
                                >
                                  ◀
                                </button>
                                <span className="score-input score-input--display" tabIndex={-1}>
                                  {splitPayload.home}
                                </span>
                                <button
                                  type="button"
                                  className="score-arrow score-arrow-right"
                                  onClick={() =>
                                    handleSplitSecondChange(
                                      game.gameId,
                                      clampScore(splitPayload.home + 1),
                                      splitPayload.away,
                                      splitPayload.weight
                                    )
                                  }
                                  aria-label="Increase split home score"
                                >
                                  ▶
                                </button>
                              </div>
                              <span className="split-editor__dash" aria-hidden="true" />
                              <div className="score-control score-control--compact">
                                <button
                                  type="button"
                                  className="score-arrow score-arrow-left"
                                  onClick={() =>
                                    handleSplitSecondChange(
                                      game.gameId,
                                      splitPayload.home,
                                      clampScore(splitPayload.away - 1),
                                      splitPayload.weight
                                    )
                                  }
                                  disabled={splitPayload.away === 0}
                                  aria-label="Decrease split away score"
                                >
                                  ◀
                                </button>
                                <span className="score-input score-input--display" tabIndex={-1}>
                                  {splitPayload.away}
                                </span>
                                <button
                                  type="button"
                                  className="score-arrow score-arrow-right"
                                  onClick={() =>
                                    handleSplitSecondChange(
                                      game.gameId,
                                      splitPayload.home,
                                      clampScore(splitPayload.away + 1),
                                      splitPayload.weight
                                    )
                                  }
                                  aria-label="Increase split away score"
                                >
                                  ▶
                                </button>
                              </div>
                            </>
                          ) : (
                            <span className="split-editor__scoreline">
                              {splitPayload.home} {splitPayload.away}
                            </span>
                          )}
                        </div>
                        <div className="split-editor__weight">
                          {canEditPicks ? (
                            <>
                              <button
                                type="button"
                                className="score-arrow score-arrow-left"
                                onClick={() =>
                                  handleSplitSecondChange(
                                    game.gameId,
                                    splitPayload.home,
                                    splitPayload.away,
                                    splitPayload.weight - 1
                                  )
                                }
                                disabled={splitPayload.weight <= SPLIT_SECOND_WEIGHT_MIN}
                                aria-label="Decrease split points"
                              >
                                ◀
                              </button>
                              <span className="split-weight-chip split-weight-chip--second">
                                {splitPayload.weight} pts
                              </span>
                              <button
                                type="button"
                                className="score-arrow score-arrow-right"
                                onClick={() =>
                                  handleSplitSecondChange(
                                    game.gameId,
                                    splitPayload.home,
                                    splitPayload.away,
                                    splitPayload.weight + 1
                                  )
                                }
                                disabled={splitPayload.weight >= SPLIT_SECOND_WEIGHT_MAX}
                                aria-label="Increase split points"
                              >
                                ▶
                              </button>
                            </>
                          ) : (
                            <span className="split-weight-chip split-weight-chip--second">
                              {splitPayload.weight} pts
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {bountyOnMatch && bountyStake != null && (
                      <div className="bounty-stake" data-testid="bounty-stake">
                        <span className="bounty-stake__label">Bounty</span>
                        {canEditPicks ? (
                          <div className="bounty-stake__stepper">
                            <button
                              type="button"
                              className="score-arrow score-arrow-left"
                              onClick={() => handleBountyStakeChange(game.gameId, bountyStake - 1)}
                              disabled={bountyStake <= BOUNTY_STAKE_MIN}
                              aria-label="Decrease bounty stake"
                            >
                              ◀
                            </button>
                            <span className="bounty-stake__value">{bountyStake} pts</span>
                            <button
                              type="button"
                              className="score-arrow score-arrow-right"
                              onClick={() => handleBountyStakeChange(game.gameId, bountyStake + 1)}
                              disabled={bountyStake >= bountyStakeCap}
                              aria-label="Increase bounty stake"
                            >
                              ▶
                            </button>
                          </div>
                        ) : (
                          <span className="bounty-stake__value">{bountyStake} pts</span>
                        )}
                        {!isRoundFinished && (
                          <span className="bounty-stake__hint">
                            {seasonStack} Stack. Consumed on Save. +{bountyStake} if 1×2 hits.
                          </span>
                        )}
                      </div>
                    )}

                    <MatchEventsSection game={game} withCenterAugment={showMatchAugmentSlots} />

                    {(selectedRound.clashTargets?.length ?? 0) > 0 && (
                      <ClashOpponentChips
                        clashTargets={selectedRound.clashTargets}
                        bets={selectedRound.bets}
                        gameId={game.gameId}
                        disabled={!canEditPicks}
                        inventory={user?.inventory}
                        onOpenTarget={(t) => {
                          const { label, embedsCompetition } = formatClashLeagueDisplay(
                            t.leagueName,
                            t.leagueId
                          );
                          setClashSheet({
                            gameId: game.gameId,
                            augmentMapKey: t.augmentMapKey,
                            opponentUsername: t.opponentUsername,
                            clashLeagueName: label,
                            hideCompetitionLabel: embedsCompetition
                          });
                        }}
                      />
                    )}

                    {gameFinished && !gameVoided && hasPlacedPick && (
                      <div className="game-points">
                        <span
                          className={`points-badge ${(bet.points ?? 0) > 0 ? 'points-badge--win' : 'points-badge--zero'}`}
                        >
                          {bet.points ?? 0} pts
                        </span>
                        <PointsBreakdown
                          predictedHome={bet.predictedHomeScore}
                          predictedAway={bet.predictedAwayScore}
                          game={game}
                          augment={bet.augment}
                          pickStats={game.pickStats}
                          playerVotedForMatch={voteOn}
                          expectedPoints={bet.points ?? 0}
                          augmentApplied={getMatchAugmentApplied(bet)}
                          augmentPoints={bet.augmentPoints}
                          inventory={user?.inventory}
                        />
                      </div>
                    )}

                    {showScoredPickUi && (
                      <GameStatsRecap
                        pickStats={game.pickStats}
                        homeScore={game.homeScore}
                        awayScore={game.awayScore}
                        playerVotedForMatch={voteOn}
                        hasMultiplierAugment={
                          getAugmentBaseCode(matchAugment) === 'm' &&
                          getMatchAugmentApplied(bet) !== false
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="betting-scroll-top">
              <button
                type="button"
                className="betting-scroll-top-btn"
                onClick={scrollToTop}
                aria-label="Scroll to top"
              >
                <svg
                  className="betting-scroll-top-btn__icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                <span className="betting-scroll-top-btn__label">Back to top</span>
              </button>
            </div>
          </form>
          </div>

          <div className="betting-rules">
            <p>
              <strong>Scoring:</strong>{' '}
              <img src={OUTCOME_ICON_SVG} alt="" className="rules-inline-icon" /> 10 pts outcome
              {' • '}
              <img src={EXACT_ICON_SVG} alt="" className="rules-inline-icon" /> +10 pts exact score
            </p>
            <p><strong>Plays:</strong> {tacticalPointsCap} tactical points per round (match plays + optional round play) • one match play per game, each type once</p>
            <p className="rules-lock">All picks lock at the first kickoff of the round</p>
          </div>
        </>
      )}

      {/* Touch drag ghost element - rendered via portal */}
      {clashSheet && clashSheetGame && selectedRound ? (
        <ClashAugmentSheet
          open
          onClose={() => setClashSheet(null)}
          competitionLabel={
            clashSheet.hideCompetitionLabel ? null : selectedLeague?.name
          }
          clashLeagueName={clashSheet.clashLeagueName}
          game={clashSheetGame}
          augmentMapKey={clashSheet.augmentMapKey}
          opponentUsername={clashSheet.opponentUsername}
          bets={selectedRound.bets}
          gameId={clashSheet.gameId}
          disabled={clashSheetDisabled}
          tacticalSpentOnThisKey={clashSheetTacticalOnKey}
          leagueCode={selectedLeague?.code}
          roundGames={selectedRound.games}
          inventory={user?.inventory}
          onStyleChange={selectMatchPoolStyle}
          onPick={(raw, targetGameId, dragData) => {
            const gid = targetGameId != null ? targetGameId : clashSheet.gameId;
            if (raw === '' || raw == null) {
              handleClashAugmentPick(gid, clashSheet.augmentMapKey, '');
              return;
            }
            const base = String(raw);
            const ref = resolvePlacedAugmentRef(
              base,
              dragData ?? { source: 'pool', value: base, styleId: getMatchPoolStyleId(base) ?? null },
              user?.inventory ?? {}
            );
            handleClashAugmentPick(gid, clashSheet.augmentMapKey, ref);
          }}
        />
      ) : null}

      {isTouchDragging && touchCurrentPos && draggingAugment ? (
        <TouchDragGhost
          x={touchCurrentPos.x}
          y={touchCurrentPos.y}
          sourceEl={touchDragSourceElRef.current}
        />
      ) : null}
    </div>
  );
};

export default Betting;
